import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import AdminNav from '../../components/admin/AdminNav';
import ExcelTable from '../../components/admin/ExcelTable';
import ApprovalSearchBar from '../../components/admin/ApprovalSearchBar';
import ApprovalFilterPanel from '../../components/admin/ApprovalFilterPanel';
import PyeAIChat from '../../components/admin/PyeAIChat';
import UserSubmissionsModal from '../../components/UserSubmissionsModal';
import AdminEditLeadModal from '../../components/admin/AdminEditLeadModal';
import DuplicateReviewModal from '../../components/admin/DuplicateReviewModal';
import ActivityLogModal from '../../components/admin/ActivityLogModal';
import { formatDate } from '../../utils/dateHelper';

const ApprovalWorkflow = ({ backendUrl }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accounts } = useMsal();
  
  // Get logged-in admin email for "My Leads" filter
  // Priority: 1) Microsoft account 2) Stored email from localStorage (for password auth)
  const msalEmail = (accounts?.[0]?.username || '').trim().toLowerCase();
  const [storedAdminEmail, setStoredAdminEmail] = useState(() => {
    return localStorage.getItem('adminFilterEmail') || '';
  });
  const adminEmail = msalEmail || storedAdminEmail;
  
  // For password-auth users to set their email
  const [showEmailPicker, setShowEmailPicker] = useState(false);
  const [emailPickerInput, setEmailPickerInput] = useState(storedAdminEmail);

  // Core state
  const [inspections, setInspections] = useState([]);
  const [myLeadsOnly, setMyLeadsOnly] = useState(false); // "My Leads" toggle
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [debugModal, setDebugModal] = useState(null); // { type: 'ai' | 'gps', row }
  const [debugRuns, setDebugRuns] = useState({ loading: false, error: null, data: null });
  const [debugPrompt, setDebugPrompt] = useState({ loading: false, error: null, data: null });
  const [rerunReason, setRerunReason] = useState('');
  const [rerunActivate, setRerunActivate] = useState(false);

  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState(20); // Default to 20 for faster initial load
  const [totalInspections, setTotalInspections] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // Current page number (1-indexed)

  // Track AdminNav mode
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Branch dropdown state
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(null); // {rowId, branches[]}
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Filter state - comprehensive
  const [filters, setFilters] = useState(() => {
    // Parse URL query params for initial filter values
    const params = new URLSearchParams(location.search);
    return {
      status: params.get('status') || 'all',
      duplicateStatus: params.get('duplicate_status') || 'all',
      query: '',
      searchMode: 'keyword',
      captureDateFrom: {},
      lastInspectionFrom: {},
      nextDueFrom: {},
      states: [],
      city: '',
      zips: '',
      nearestBranches: [],
      submitter: '',
      provider: '',
      periods: [],
      hasInvalidData: false,
      salesStatus: params.get('sales_status') || '',
      rvpName: params.get('rvp_name') || '',
      rdName: params.get('rd_name') || ''
    };
  });

  // AI interpretation state
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [aiAnswer, setAiAnswer] = useState(null); // For counting/aggregation questions

  // Pye AI Chat state
  const [showPyeChat, setShowPyeChat] = useState(false);

  // Available filter options (populated from data)
  const [availableOptions, setAvailableOptions] = useState({
    states: [],
    branches: [],
    submitters: [],
    providers: [],
    rvps: [],
    rds: []
  });

  // Legacy table filters (for ExcelTable compatibility)
  const [tableFilters, setTableFilters] = useState({});

  const safeParseGeminiResponse = (row) => {
    try {
      const responseText = typeof row?.gemini_response === 'string'
        ? row.gemini_response
        : JSON.stringify(row?.gemini_response);
      const jsonMatch = responseText?.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
      if (jsonMatch) return JSON.parse(jsonMatch[1] || jsonMatch[2]);
      return null;
    } catch (_) {
      return null;
    }
  };

  const getAiDateStatus = (row) => {
    const parsed = safeParseGeminiResponse(row);
    if (!parsed) return { status: 'unknown', reason: 'No AI JSON parsed' };

    const nextDueText = parsed?.next_due_date?.extracted_text || '';
    const lastText = parsed?.last_inspection_date?.extracted_text || '';

    if (typeof nextDueText === 'string' && nextDueText.includes('FALLBACK')) {
      return { status: 'fallback', reason: nextDueText || 'Fallback triggered' };
    }

    // If we have numeric Y/M/D, treat as extracted (even if OCR confidence is low)
    const y = parsed?.last_inspection_date?.year;
    const m = parsed?.last_inspection_date?.month;
    const d = parsed?.last_inspection_date?.day;
    if (Number.isInteger(y) && Number.isInteger(m) && Number.isInteger(d)) {
      return { status: 'extracted', reason: lastText || 'Extracted' };
    }

    return { status: 'unknown', reason: lastText || 'Unknown' };
  };

  // Load run history + prompt when AI debug modal opens
  useEffect(() => {
    const load = async () => {
      if (!debugModal?.row?._id || debugModal.type !== 'ai') {
        setDebugRuns({ loading: false, error: null, data: null });
        setDebugPrompt({ loading: false, error: null, data: null });
        setRerunReason('');
        setRerunActivate(false);
        return;
      }

      const row = debugModal.row;
      setDebugRuns({ loading: true, error: null, data: null });
      setDebugPrompt({ loading: true, error: null, data: null });

      try {
        const token = localStorage.getItem('token');
        const runsRes = await fetch(`${backendUrl}/api/admin/inspections/${row._id}/analysis-runs`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        const runsJson = await runsRes.json();
        if (!runsRes.ok || !runsJson?.success) throw new Error(runsJson?.detail || 'Failed to load runs');
        setDebugRuns({ loading: false, error: null, data: runsJson });
      } catch (e) {
        setDebugRuns({ loading: false, error: e.message || 'Failed to load runs', data: null });
      }

      try {
        const promptId = row.prompt_id || 'forensic_unit_v1';
        const promptToken = localStorage.getItem('token');
        const pRes = await fetch(`${backendUrl}/api/admin/prompts/${encodeURIComponent(promptId)}`, {
          headers: {
            ...(promptToken && { 'Authorization': `Bearer ${promptToken}` })
          }
        });
        const pJson = await pRes.json();
        if (!pRes.ok || !pJson?.success) throw new Error(pJson?.detail || 'Failed to load prompt');
        setDebugPrompt({ loading: false, error: null, data: pJson });
      } catch (e) {
        setDebugPrompt({ loading: false, error: e.message || 'Failed to load prompt', data: null });
      }
    };

    load();
  }, [debugModal, backendUrl]);

  // Listen for AdminNav toggle events
  useEffect(() => {
    const handleNavToggle = (e) => {
      // Support both new 'mode' and legacy 'isCollapsed' for safety
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        // Fallback if event only sending isCollapsed
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  // Initialize filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFilters = {};

    // Parse URL parameters
    if (params.get('status')) urlFilters.status = params.get('status');
    if (params.get('duplicate_status')) urlFilters.duplicateStatus = params.get('duplicate_status');
    if (params.get('sales_status')) urlFilters.salesStatus = params.get('sales_status');
    if (params.get('q')) urlFilters.query = params.get('q');
    if (params.get('capture_from')) {
      urlFilters.captureDateFrom = { from: params.get('capture_from'), to: params.get('capture_to') || '' };
    }
    if (params.get('last_from')) {
      urlFilters.lastInspectionFrom = { from: params.get('last_from'), to: params.get('last_to') || '' };
    }
    if (params.get('next_from')) {
      urlFilters.nextDueFrom = { from: params.get('next_from'), to: params.get('next_to') || '' };
    }
    if (params.get('states')) urlFilters.states = params.get('states').split(',');
    if (params.get('city')) urlFilters.city = params.get('city');
    if (params.get('zips')) urlFilters.zips = params.get('zips');
    if (params.get('branches')) urlFilters.nearestBranches = params.get('branches').split(',');
    if (params.get('submitter')) urlFilters.submitter = params.get('submitter');
    if (params.get('provider')) urlFilters.provider = params.get('provider');
    if (params.get('periods')) urlFilters.periods = params.get('periods').split(',');
    if (params.get('has_invalid')) urlFilters.hasInvalidData = params.get('has_invalid') === 'true';
    if (params.get('rvp_name')) urlFilters.rvpName = params.get('rvp_name');
    if (params.get('rd_name')) urlFilters.rdName = params.get('rd_name');

    if (Object.keys(urlFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...urlFilters }));
    }
  }, []); // Only on mount

  // Fetch regional officers for RVP/RD filter dropdowns (once on mount)
  useEffect(() => {
    const fetchOfficers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${backendUrl}/api/admin/regional-officers`, {
          headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
        });
        const data = await response.json();
        if (data.success && data.officers) {
          const rvps = data.officers
            .filter(o => o.role === 'regional_sales_leader' && o.active !== false)
            .map(o => ({ id: o.id, name: o.name, email: o.email }))
            .sort((a, b) => a.name.localeCompare(b.name));
          const rds = data.officers
            .filter(o => o.role === 'area_sales_manager' && o.active !== false)
            .map(o => {
              const parent = data.officers.find(p => p.id === o.reports_to);
              return { id: o.id, name: o.name, email: o.email, reportsToName: parent?.name || '' };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
          setAvailableOptions(prev => ({ ...prev, rvps, rds }));
        }
      } catch (err) {
        console.error('Failed to fetch regional officers for filters:', err);
      }
    };
    fetchOfficers();
  }, [backendUrl]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    // Add all active filters to URL
    if (filters.status && filters.status !== 'all') params.set('status', filters.status);
    if (filters.duplicateStatus && filters.duplicateStatus !== 'all') params.set('duplicate_status', filters.duplicateStatus);
    if (filters.salesStatus) params.set('sales_status', filters.salesStatus);
    if (filters.query) params.set('q', filters.query);

    // Date ranges
    if (filters.captureDateFrom?.from) {
      params.set('capture_from', filters.captureDateFrom.from);
      if (filters.captureDateFrom.to) params.set('capture_to', filters.captureDateFrom.to);
    }
    if (filters.lastInspectionFrom?.from) {
      params.set('last_from', filters.lastInspectionFrom.from);
      if (filters.lastInspectionFrom.to) params.set('last_to', filters.lastInspectionFrom.to);
    }
    if (filters.nextDueFrom?.from) {
      params.set('next_from', filters.nextDueFrom.from);
      if (filters.nextDueFrom.to) params.set('next_to', filters.nextDueFrom.to);
    }

    // Location
    if (filters.states?.length > 0) params.set('states', filters.states.join(','));
    if (filters.city) params.set('city', filters.city);
    if (filters.zips) params.set('zips', filters.zips);
    if (filters.nearestBranches?.length > 0) params.set('branches', filters.nearestBranches.join(','));

    // Submitter & Provider
    if (filters.submitter) params.set('submitter', filters.submitter);
    if (filters.provider) params.set('provider', filters.provider);

    // Misc
    if (filters.periods?.length > 0) params.set('periods', filters.periods.join(','));
    if (filters.hasInvalidData) params.set('has_invalid', 'true');

    // Sales leadership
    if (filters.rvpName) params.set('rvp_name', filters.rvpName);
    if (filters.rdName) params.set('rd_name', filters.rdName);

    // Update URL without page reload
    const newSearch = params.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    if (newUrl !== location.pathname + location.search) {
      navigate(newUrl, { replace: true });
    }
  }, [filters, navigate, location.pathname]);

  // Fetch inspections when filters, page, or page size changes
  useEffect(() => {
    fetchInspections();
  }, [filters, itemsPerPage, currentPage]);

  const fetchInspections = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.set('status', filters.status || 'pending');
      if (filters.duplicateStatus && filters.duplicateStatus !== 'all') {
        params.set('duplicate_status', filters.duplicateStatus);
      }
      params.set('page', currentPage.toString());
      params.set('limit', itemsPerPage.toString());

      // Add search query and mode
      if (filters.query) {
        params.set('q', filters.query);
        params.set('search_mode', filters.searchMode || 'keyword');
      }

      // Date ranges
      if (filters.captureDateFrom?.from) {
        params.set('capture_from', filters.captureDateFrom.from);
        if (filters.captureDateFrom.to) params.set('capture_to', filters.captureDateFrom.to);
      }
      if (filters.lastInspectionFrom?.from) {
        params.set('last_from', filters.lastInspectionFrom.from);
        if (filters.lastInspectionFrom.to) params.set('last_to', filters.lastInspectionFrom.to);
      }
      if (filters.nextDueFrom?.from) {
        params.set('next_from', filters.nextDueFrom.from);
        if (filters.nextDueFrom.to) params.set('next_to', filters.nextDueFrom.to);
      }

      // Location filters
      if (filters.states?.length > 0) params.set('states', filters.states.join(','));
      if (filters.city) params.set('city', filters.city);
      if (filters.zips) params.set('zips', filters.zips);
      if (filters.nearestBranches?.length > 0) params.set('branches', filters.nearestBranches.join(','));

      // Submitter & Provider
      if (filters.submitter) params.set('submitter', filters.submitter);
      if (filters.provider) params.set('provider', filters.provider);

      // Misc
      if (filters.periods?.length > 0) params.set('periods', filters.periods.join(','));
      if (filters.hasInvalidData) params.set('has_invalid', 'true');
      
      // Sales pipeline status filter
      if (filters.salesStatus) params.set('sales_status', filters.salesStatus);

      // Sales leadership filters
      if (filters.rvpName) params.set('rvp_name', filters.rvpName);
      if (filters.rdName) params.set('rd_name', filters.rdName);

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/inspections?${params.toString()}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success) {
        const pageInspections = data.inspections || [];
        setTotalInspections(data.pagination?.total || pageInspections.length);
        setInspections(pageInspections);

        // Capture AI interpretation if available
        if (data.ai_interpreted_filters) {
          const query = filters.query.toLowerCase();
          const hasFilters = Object.keys(data.ai_interpreted_filters).length > 0;

          // Always set interpretation (even if empty) so banner shows
          setAiInterpretation(data.ai_interpreted_filters);

          // Check if this was a counting question
          if (query.includes('how many') || query.includes('how much') || query.includes('count')) {
            const count = data.inspections?.length || 0;
            const submitter = data.ai_interpreted_filters.submitted_by || 'matching';
            setAiAnswer(`${submitter} has ${count} submission${count !== 1 ? 's' : ''}`);
          } else if (!hasFilters) {
            // AI couldn't find data filters - it's a conversational question
            const conversationalResponses = {
              'how are you': "I'm doing great! Ready to help you search through inspection data! ✨",
              'hello': "Hello! I'm your AI search assistant. Ask me anything about inspections!",
              'hi': "Hi there! Need help finding something?",
              'what can you do': "I can search inspections using natural language! Try: 'show me Matt's submissions' or 'pending items in Georgia'",
              'help': "Ask me questions like: 'How many submissions does Shiva have?' or 'Show me approved inspections in Florida'"
            };

            const matchedResponse = Object.entries(conversationalResponses).find(([key]) =>
              query.includes(key)
            );

            if (matchedResponse) {
              setAiAnswer(matchedResponse[1]);
              // Keep empty interpretation to show the banner
            } else {
              setAiAnswer("I didn't find any matching data. Try asking about submissions, locations, or dates!");
            }
          } else {
            setAiAnswer(null);
          }
        } else {
          setAiInterpretation(null);
          setAiAnswer(null);
        }

        // Extract available options from data
        extractAvailableOptions(data.inspections || []);
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique values for filter dropdowns
  const extractAvailableOptions = (data) => {
    const states = [...new Set(data.map(i => i.site_state).filter(Boolean))].sort();
    const cities = [...new Set(data.map(i => i.site_city).filter(Boolean))].sort();
    const branches = [...new Set(data.map(i => i.assigned_branch?.name).filter(Boolean))].sort();
    const submitters = [...new Set(data.map(i => i.submitted_by).filter(Boolean))].sort();
    const providers = [...new Set(data.map(i => i.current_provider).filter(Boolean))].sort();

    setAvailableOptions(prev => ({ ...prev, states, cities, branches, submitters, providers }));
  };

  const handleApprove = async (inspectionId) => {
    try {
      console.log(`Approving inspection: ${inspectionId}`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/approve/${inspectionId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ approved_by: 'Admin' })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Approve response received');

      if (data.success) {
        await fetchInspections(); // Refresh
        alert('✅ Tag lead approved successfully!');
      } else {
        alert(`❌ Failed to approve: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving:', error);
      alert(`❌ Failed to approve: ${error.message}`);
    }
  };

  const handleReject = async (inspectionId) => {
    const reason = window.prompt('Reason for rejection (optional):') || 'No reason provided';

    try {
      console.log(`Rejecting inspection: ${inspectionId}, reason: ${reason}`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/reject/${inspectionId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          reason: reason,
          rejected_by: 'Admin'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Reject response received');

      if (data.success) {
        await fetchInspections(); // Refresh
        alert('❌ Tag lead rejected successfully!');
      } else {
        alert(`❌ Failed to reject: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      alert(`❌ Failed to reject: ${error.message}`);
    }
  };

  const handleUndo = async (inspectionId, currentStatus) => {
    const confirmMsg = currentStatus === 'approved'
      ? 'Move this approved tag lead back to pending?'
      : 'Move this rejected tag lead back to pending?';

    if (!window.confirm(confirmMsg)) return;

    try {
      console.log(`Undoing inspection: ${inspectionId}, from: ${currentStatus}`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/undo/${inspectionId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ previous_status: currentStatus })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Undo response received');

      if (data.success) {
        await fetchInspections(); // Refresh
        alert('↩️ Status reset to pending successfully!');
      } else {
        alert(`❌ Failed to undo: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error undoing:', error);
      alert(`❌ Failed to undo: ${error.message}`);
    }
  };

  // Bulk delete inspections (move to trash)
  const handleBulkDelete = async (inspectionIds) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/inspections/bulk-delete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ inspection_ids: inspectionIds })
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${data.deleted_count} inspection(s) moved to trash`);
        fetchInspections(); // Refresh list

        if (data.failed_ids && data.failed_ids.length > 0) {
          alert(`⚠️ ${data.failed_ids.length} inspection(s) failed to delete`);
        }
      } else {
        alert(`❌ Failed to delete inspections`);
      }
    } catch (error) {
      console.error('Error deleting inspections:', error);
      alert(`❌ Failed to delete: ${error.message}`);
    }
  };

  // Bulk archive inspections
  const handleBulkArchive = async (inspectionIds) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/inspections/bulk-archive`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ inspection_ids: inspectionIds })
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${data.archived_count} inspection(s) archived`);
        fetchInspections(); // Refresh list

        if (data.failed_ids && data.failed_ids.length > 0) {
          alert(`⚠️ ${data.failed_ids.length} inspection(s) failed to archive`);
        }
      } else {
        alert(`❌ Failed to archive inspections`);
      }
    } catch (error) {
      console.error('Error archiving inspections:', error);
      alert(`❌ Failed to archive: ${error.message}`);
    }
  };

  // Fetch nearby branches for dropdown
  const fetchNearbyBranches = async (rowId, gpsData) => {
    try {
      setLoadingBranches(true);
      let lat, lng;

      if (typeof gpsData === 'string') {
        const parsed = JSON.parse(gpsData);
        lat = parsed.latitude;
        lng = parsed.longitude;
      } else if (gpsData) {
        lat = gpsData.latitude;
        lng = gpsData.longitude;
      }

      if (!lat || !lng) {
        alert('⚠️ No GPS data available for this inspection');
        setBranchDropdownOpen(null);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/affiliates/nearest?lat=${lat}&lng=${lng}&limit=5`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success && data.offices) {
        setBranchDropdownOpen({ rowId, branches: data.offices });
      } else {
        alert('⚠️ Could not fetch nearby branches');
        setBranchDropdownOpen(null);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      alert(`❌ Failed to fetch branches: ${error.message}`);
      setBranchDropdownOpen(null);
    } finally {
      setLoadingBranches(false);
    }
  };

  // Export SELECTED inspections
  const handleExportSelected = (selectedIds) => {
    const selectedData = inspections.filter(i => selectedIds.includes(i.id));
    exportInspectionsToCSV(selectedData, 'selected', 'csv');
  };

  // Export data with current filters (supports CSV, Excel, JSON)
  const handleExport = (format = 'csv') => {
    const filteredData = getFilteredInspections();
    exportInspectionsToCSV(filteredData, 'filtered', format);
  };

  // NEW FEATURE: Distribute leads to Regional Vice Presidents via email
  const handleDistributeToRDs = async () => {
    if (!window.confirm(
      `This will:\n\n` +
      `1. Export all APPROVED leads\n` +
      `2. Remove duplicates (same address + business name)\n` +
      `3. Send one CSV per Regional Vice President via email\n` +
      `4. Mark leads as sent\n\n` +
      `Continue?`
    )) {
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      // Show preview first
      const previewResponse = await fetch(
        `${backendUrl}/api/admin/distribution-preview?status_filter=approved`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!previewResponse.ok) {
        throw new Error('Failed to generate preview');
      }

      const preview = await previewResponse.json();

      // Ask for mode (Safe Test Default)
      const modePrompt = 
        `Distribution Preview:\n\n` +
        `Total Leads: ${preview.total_leads}\n` +
        `After De-duplication: ${preview.original_leads}\n` +
        `Duplicates Removed: ${preview.duplicate_leads}\n` +
        `Unassigned: ${preview.unassigned_leads}\n\n` +
        `Will email ${preview.regional_directors.length} Regional Vice Presidents:\n` +
        preview.regional_directors
          .map(rd => `  • ${rd.name}: ${rd.lead_count} leads`)
          .join('\n') +
        `\n\n🛡️ SAFETY CHECK:\n` +
        `Type 'TEST' to send all 20+ emails to YOURSELF (Safe)\n` +
        `Type 'LIVE' to send to actual Regional Vice Presidents (Danger)\n\n` +
        `Action:`;

      const modeInput = window.prompt(modePrompt, "TEST");

      if (modeInput === null) {
        setLoading(false);
        return;
      }

      const isTest = modeInput.toUpperCase().trim() !== 'LIVE';
      
      if (!isTest) {
        if (!window.confirm("⚠️ ARE YOU SURE? This will email real Regional Vice Presidents. This cannot be undone.")) {
            setLoading(false);
            return;
        }
      }

      // Execute distribution
      const response = await fetch(
        `${backendUrl}/api/admin/distribute-leads-to-rds?status_filter=approved&test_mode=${isTest}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Distribution failed');
      }

      const report = await response.json();
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);

      if (report.success) {
        alert(
          `✅ Distribution Complete! (${duration}s)\n\n` +
          `Leads Distributed: ${report.distributed_leads}\n` +
          `Emails Sent: ${report.emails_sent}\n` +
          `Emails Failed: ${report.emails_failed}\n` +
          `Leads Marked Sent: ${report.leads_marked_sent}\n` +
          `Duplicates Removed: ${report.duplicate_leads}\n` +
          `Unassigned: ${report.unassigned_leads}\n\n` +
          `Details:\n` +
          report.regional_directors
            .map(rd => `  • ${rd.name}: ${rd.lead_count} leads ${rd.sent ? '✅' : '❌'}`)
            .join('\n')
        );

        // Refresh inspections to show updated sent_in_digest status
        fetchInspections();
      } else {
        throw new Error(report.errors?.join(', ') || 'Unknown error');
      }

    } catch (error) {
      console.error('Distribution error:', error);
      alert(`❌ Distribution failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  // Shared export logic
  const exportInspectionsToCSV = (dataToExport, exportType = 'filtered', format = 'csv') => {

    const headers = [
      'Inspection ID', 'Image URL', 'Capture Date', 'Site Company', 'Street Address', 'City', 'State', 'Zip',
      'Last Inspection', 'Next Due', 'Condition', 'Requires Attention', 'Current Provider',
      'Provider Phone', 'Provider Address', 'Extinguisher Type', 'Service Details',
      'Equipment Numbers', 'AI Confidence', 'GPS Coordinates', 'GPS Source', 'Region', 'Nearest Branch', 
      'Submitted By', 'Submitter Email', 'Regional Vice President', 'Regional Director', 
      'Notes', 'Attachments Count', 'Email Sent', 'Status', 'Approval Status',
      'Activity Count', 'Latest Activity', 'Latest Activity Date', 'Latest Activity Actor',
      'Sales Status', 'Sales Value', 'Sales Notes', 'Lost Reason', 'Sales Updated At',
      'Smarty Enriched', 'Property Owner', 'Building Sqft', 'Fire Sprinklers', 'Stories', 'Year Built', 'Land Use', 'Property Value', 'Smarty Cost'
    ];

    const rows = dataToExport.map(i => {
      // Parse gemini_response for forensic data
      let parsedData = {};
      try {
        const responseText = typeof i.gemini_response === 'string'
          ? i.gemini_response
          : JSON.stringify(i.gemini_response);
        const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
        if (jsonMatch) {
          parsedData = JSON.parse(jsonMatch[1] || jsonMatch[2]);
        }
      } catch (e) {
        // Silently fail
      }

      // Extract forensic fields
      const condition = parsedData.condition || '';
      const requiresAttention = parsedData.requires_attention ? 'Yes' : 'No';
      const extinguisherType = parsedData.extinguisher_type || '';
      const serviceDetails = parsedData.service_details?.service_type || '';
      const providerPhone = i.service_company_phone || i.working_service_company_phone || parsedData.service_company?.phone || '';
      const providerAddress = parsedData.service_company?.address || '';
      const confidenceScore = parsedData.confidence_score ? `${Math.round(parsedData.confidence_score * 100)}%` : '';

      // Equipment numbers
      const equipment = parsedData.equipment_numbers || {};
      const equipmentNumbers = [
        equipment.ae_number && equipment.ae_number !== 'unknown' ? `AE:${equipment.ae_number}` : '',
        equipment.he_number && equipment.he_number !== 'unknown' ? `HE:${equipment.he_number}` : '',
        equipment.ee_number && equipment.ee_number !== 'unknown' ? `EE:${equipment.ee_number}` : '',
        equipment.fe_number && equipment.fe_number !== 'unknown' ? `FE:${equipment.fe_number}` : ''
      ].filter(Boolean).join(' ') || '';

      // Collect all notes (text, voice, additional)
      const allNotes = [];
      if (i.notes && i.notes.trim()) {
        // Clean up - remove "Submitted by:" prefix
        let cleanNotes = i.notes;
        const submittedByMatch = i.notes.match(/Submitted by:\s*[^\n]+\n?/i);
        if (submittedByMatch) {
          cleanNotes = i.notes.replace(submittedByMatch[0], '').trim();
        }
        if (cleanNotes) allNotes.push(cleanNotes);
      }
      if (i.main_page_voice_notes && i.main_page_voice_notes.trim()) {
        allNotes.push(`[Voice] ${i.main_page_voice_notes.trim()}`);
      }
      if (i.additional_notes && i.additional_notes.trim()) {
        allNotes.push(`[Additional] ${i.additional_notes.trim()}`);
      }
      const combinedNotes = allNotes.join(' | ');
      
      // Extract GPS coordinates and source
      let gpsCoords = '';
      let gpsSource = '';
      if (i.gps_data) {
        try {
          const gps = typeof i.gps_data === 'string' ? JSON.parse(i.gps_data) : i.gps_data;
          if (gps.latitude && gps.longitude) {
            gpsCoords = `${gps.latitude.toFixed(6)}, ${gps.longitude.toFixed(6)}`;
            gpsSource = gps.source || 'device';
          }
        } catch (e) {
          gpsCoords = 'GPS error';
        }
      }
      
      // Map state to region
      const STATE_TO_REGION = {
        'TX': 'Southwest', 'OK': 'Southwest', 'AR': 'Southwest', 'LA': 'Southwest',
        'GA': 'Southeast', 'FL': 'Southeast', 'AL': 'Southeast', 'MS': 'Southeast', 
        'TN': 'Southeast', 'SC': 'Southeast', 'NC': 'Southeast',
        'PA': 'Mid-Atlantic', 'NY': 'Mid-Atlantic', 'NJ': 'Mid-Atlantic', 
        'MD': 'Mid-Atlantic', 'VA': 'Mid-Atlantic', 'WV': 'Mid-Atlantic', 'DE': 'Mid-Atlantic',
        'OH': 'Midwest', 'IN': 'Midwest', 'IL': 'Midwest', 'MI': 'Midwest', 
        'WI': 'Midwest', 'MN': 'Midwest', 'IA': 'Midwest', 'MO': 'Midwest',
        'MA': 'Northeast', 'CT': 'Northeast', 'RI': 'Northeast', 
        'VT': 'Northeast', 'NH': 'Northeast', 'ME': 'Northeast',
        'CA': 'West', 'NV': 'West', 'AZ': 'West', 'UT': 'West', 
        'CO': 'West', 'NM': 'West', 'WA': 'West', 'OR': 'West',
        'KY': 'South', 'PR': 'South'
      };
      const state = (i.site_state || '').toUpperCase().trim();
      const region = STATE_TO_REGION[state] || '';

      return [
        i.id || i._id || '',
        i.image_url || '',
        i.capture_date || i.created_at || '',
        i.business_name || '',
        i.site_street_address || '',
        i.site_city || '',
        i.site_state || '',
        i.site_zip || '',
        i.last_inspection_date || '',
        i.next_due_date || '',
        condition,
        requiresAttention,
        i.current_provider || '',
        providerPhone,
        providerAddress,
        extinguisherType,
        serviceDetails,
        equipmentNumbers,
        confidenceScore,
        gpsCoords,
        gpsSource,
        region,
        i.assigned_branch?.name || '',
        i.submitted_by || '',
        i.submitted_by_email || 'No email',
        i.regional_sales_leader_name || 'Unassigned',
        i.area_sales_manager_name || 'Direct to RVP',
        combinedNotes,
        i.main_page_attachments?.length || 0,
        i.sent_in_digest ? `Yes (${i.digest_sent_at ? new Date(i.digest_sent_at).toLocaleDateString() : 'date unknown'})` : 'No',
        i.status || '',
        i.approval_status || i.status || '',
        // Activity Log fields
        (i.activity_log || []).length,
        (i.activity_log && i.activity_log.length > 0) ? i.activity_log[i.activity_log.length - 1].action : 'No activity',
        (i.activity_log && i.activity_log.length > 0) ? new Date(i.activity_log[i.activity_log.length - 1].timestamp).toLocaleString() : '',
        (i.activity_log && i.activity_log.length > 0) ? i.activity_log[i.activity_log.length - 1].actor : '',
        i.sales_status || 'new',
        i.sales_value ? `$${parseFloat(i.sales_value).toLocaleString()}` : '',
        i.sales_notes || '',
        i.sales_lost_reason || '',
        i.sales_updated_at ? new Date(i.sales_updated_at).toLocaleDateString() : '',
        // Smarty enrichment fields
        i.smarty_enrichment_status === 'success' ? 'Yes' : 'No',
        i.smarty_data?.owner_name || '',
        i.smarty_data?.building_sqft || '',
        i.smarty_data?.fire_sprinklers ? 'Yes' : 'No',
        i.smarty_data?.stories || '',
        i.smarty_data?.year_built || '',
        i.smarty_data?.land_use_group || '',
        i.smarty_data?.assessed_value ? `$${parseInt(i.smarty_data.assessed_value).toLocaleString()}` : '',
        i.smarty_api_cost ? `$${i.smarty_api_cost.toFixed(4)}` : ''
      ];
    });

    const neutralizeSpreadsheetCell = (value) => {
      const text = value == null ? '' : String(value);
      const trimmed = text.trimStart();
      return /^[=+\-@]/.test(trimmed) ? `'${text}` : text;
    };

    const escapeCsvCell = (value) => neutralizeSpreadsheetCell(value).replace(/"/g, '""');

    const escapeHtmlCell = (value) => neutralizeSpreadsheetCell(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const safeRows = rows.map(row => row.map(neutralizeSpreadsheetCell));

    // Generate filename
    const filterSuffix = filters.query ? `-${filters.query.replace(/[^a-z0-9]/gi, '').substring(0, 20)}` : '';
    const statusSuffix = filters.status !== 'all' ? `-${filters.status}` : '';
    const dateStr = new Date().toISOString().split('T')[0];
    const baseFilename = `tag-leads${statusSuffix}${filterSuffix}-${dateStr}`;

    if (format === 'csv') {
      const csv = [headers, ...safeRows].map(row => row.map(cell => `"${escapeCsvCell(cell)}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      downloadBlob(blob, `${baseFilename}.csv`);
    } else if (format === 'json') {
      const jsonData = dataToExport.map((item, idx) => {
        const obj = {};
        headers.forEach((header, i) => {
          obj[header] = safeRows[idx][i];
        });
        return obj;
      });
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      downloadBlob(blob, `${baseFilename}.json`);
    } else if (format === 'excel') {
      // For Excel, we'll generate a simple HTML table that Excel can open
      // In production, you'd use a library like xlsx or exceljs
      const htmlTable = `
        <html xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="UTF-8"></head>
        <body>
          <table border="1">
            <thead><tr>${headers.map(h => `<th>${escapeHtmlCell(h)}</th>`).join('')}</tr></thead>
            <tbody>
              ${safeRows.map(row => `<tr>${row.map(cell => `<td>${escapeHtmlCell(cell)}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;
      const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' });
      downloadBlob(blob, `${baseFilename}.xls`);
    }
  };

  // Helper function to download blob
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Get filtered inspections (apply table filters on top of API filters)
  const getFilteredInspections = () => {
    let result = [...inspections];

    // 🪃 "My Leads" filter - show only leads assigned to logged-in admin
    if (myLeadsOnly && adminEmail) {
      result = result.filter(row => {
        const assignedTo = (row.regional_officer_email || '').toLowerCase();
        return assignedTo === adminEmail;
      });
    }

    // Apply table-level filters (from old FilterBar if still used)
    Object.keys(tableFilters).forEach(key => {
      const filterValue = tableFilters[key];
      if (!filterValue || filterValue === '') return;

      result = result.filter(row => {
        const cellValue = row[key];
        if (cellValue == null) return false;

        if (typeof filterValue === 'string') {
          return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        }

        if (filterValue.from || filterValue.to) {
          const cellDate = new Date(cellValue);
          if (filterValue.from && cellDate < new Date(filterValue.from)) return false;
          if (filterValue.to && cellDate > new Date(filterValue.to)) return false;
        }

        return true;
      });
    });

    return result;
  };

  // Get display inspections (server already handles pagination, apply local filters only)
  const getPaginatedInspections = () => {
    return getFilteredInspections();
  };

  // Calculate total pages from server-side total
  const totalPages = Math.ceil(totalInspections / itemsPerPage);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.duplicateStatus && filters.duplicateStatus !== 'all') count++;
    if (filters.salesStatus) count++;
    if (filters.query) count++;
    if (filters.captureDateFrom?.from || filters.captureDateFrom?.to) count++;
    if (filters.lastInspectionFrom?.from || filters.lastInspectionFrom?.to) count++;
    if (filters.nextDueFrom?.from || filters.nextDueFrom?.to) count++;
    if (filters.states?.length > 0) count++;
    if (filters.city) count++;
    if (filters.zips) count++;
    if (filters.nearestBranches?.length > 0) count++;
    if (filters.submitter) count++;
    if (filters.provider) count++;
    if (filters.periods?.length > 0) count++;
    if (filters.hasInvalidData) count++;
    if (filters.rvpName) count++;
    if (filters.rdName) count++;
    return count;
  }, [filters]);

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilters({
      status: 'all',
      duplicateStatus: 'all',
      query: '',
      searchMode: 'keyword',
      captureDateFrom: {},
      lastInspectionFrom: {},
      nextDueFrom: {},
      states: [],
      city: '',
      zips: '',
      nearestBranches: [],
      submitter: '',
      provider: '',
      periods: [],
      hasInvalidData: false,
      salesStatus: '',
      rvpName: '',
      rdName: ''
    });
    setTableFilters({});
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setCurrentPage(1); // Reset to page 1 when filters change
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // State for inline editing
  const [editingCell, setEditingCell] = useState(null); // { rowId, columnKey }
  const [editValue, setEditValue] = useState('');

  // User submissions modal (clickable Submitted By)
  const [showUserSubmissionsModal, setShowUserSubmissionsModal] = useState(false);
  const [selectedUserForSubmissions, setSelectedUserForSubmissions] = useState(null);

  // Admin edit modal (popup editor for location/provider/notes)
  const [adminEditModalRow, setAdminEditModalRow] = useState(null);
  const [deepLinkOpenInspectionId, setDeepLinkOpenInspectionId] = useState(null);
  const [deepLinkReturnTo, setDeepLinkReturnTo] = useState(null);
  
  // Duplicate Review Modal state
  const [dupReviewOpen, setDupReviewOpen] = useState(false);
  const [dupReviewSeed, setDupReviewSeed] = useState(null);
  const [dupReviewGroup, setDupReviewGroup] = useState([]);

  // Activity Log Modal state
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [activityLogInspection, setActivityLogInspection] = useState(null);

  // Deep link support: /admin/approvals?openInspectionId=<uuid>&returnTo=/admin/emails
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const openId = params.get('openInspectionId');
    const returnTo = params.get('returnTo');
    if (openId) {
      setDeepLinkOpenInspectionId(openId);
      setDeepLinkReturnTo(returnTo || null);
      // Ensure we can find the inspection regardless of status filter.
      setFilters(prev => ({ ...prev, status: 'all' }));
    } else {
      setDeepLinkOpenInspectionId(null);
      setDeepLinkReturnTo(null);
    }
  }, [location.search]);

  // When inspections load, open the admin edit modal for the deep-linked inspection
  useEffect(() => {
    if (!deepLinkOpenInspectionId) return;
    if (adminEditModalRow) return;
    if (!backendUrl) return;

    const run = async () => {
      try {
        // Fetch by public UUID so deep links work even if the row isn't in the current loaded list.
        const deepLinkToken = localStorage.getItem('token');
        const res = await fetch(`${backendUrl}/api/admin/inspections/by-public-id/${encodeURIComponent(deepLinkOpenInspectionId)}`, {
          headers: {
            ...(deepLinkToken && { 'Authorization': `Bearer ${deepLinkToken}` })
          }
        });
        const data = await res.json();
        if (res.ok && data?.success && data.inspection) {
          setAdminEditModalRow(data.inspection);
          return;
        }
      } catch (e) {
        // fallback below
      }

      // Fallback: try to locate in currently loaded list
      if (Array.isArray(inspections) && inspections.length > 0) {
        const id = deepLinkOpenInspectionId;
        const match = inspections.find((r) => r?.id === id || r?._id === id);
        if (match) setAdminEditModalRow(match);
      }
    };

    run();
  }, [deepLinkOpenInspectionId, inspections, adminEditModalRow, backendUrl]);

  // Handle cell edit save
  const handleCellSave = async (rowId, columnKey, newValue) => {
    try {
      console.log('💾 SAVING:', { rowId, columnKey, newValue });

      const url = `${backendUrl}/api/admin/inspections/${rowId}`;

      // Save to backend (retry on Cosmos throttling)
      let response = null;
      let lastErrorText = '';
      for (let attempt = 0; attempt < 3; attempt++) {
        const patchToken = localStorage.getItem('token');
        response = await fetch(url, {
          method: 'PATCH',
          headers: { 
            'Content-Type': 'application/json',
            ...(patchToken && { 'Authorization': `Bearer ${patchToken}` })
          },
          body: JSON.stringify({ [columnKey]: newValue })
        });

        console.log('📡 Response status:', response.status);
        if (response.ok) break;

        lastErrorText = await response.text().catch(() => '');
        const lower = lastErrorText.toLowerCase();
        const isRateLimited = response.status === 429 || lower.includes('requestratetoolarge') || lower.includes('code\":16500') || lower.includes('code: 16500');
        if (isRateLimited && attempt < 2) {
          const delayMs = 300 * (2 ** attempt);
          await new Promise((r) => setTimeout(r, delayMs));
          continue;
        }
        break;
      }

      if (!response || !response.ok) {
        console.error('❌ Save failed:', lastErrorText);
        throw new Error(`Failed to save: ${response?.status || 'unknown'}`);
      }

      const result = await response.json();

      // If last_inspection_date was updated, backend auto-calculated next_due_date
      // Update BOTH fields in local state immediately
      if (columnKey === 'last_inspection_date' && result.updates) {
        console.log('🔄 Updating Last Insp AND Next Due in UI...');

        setInspections(prev => prev.map(inspection =>
          inspection._id === rowId
            ? {
              ...inspection,
              last_inspection_date: result.updates.last_inspection_date,
              next_due_date: result.updates.next_due_date
            }
            : inspection
        ));
      } else {
        // For other fields, just update locally
        setInspections(prev => prev.map(inspection =>
          inspection._id === rowId
            ? { ...inspection, [columnKey]: newValue }
            : inspection
        ));
      }

      setEditingCell(null);
      setEditValue('');
      console.log('✅ Edit complete');
    } catch (error) {
      console.error('❌ Error saving cell:', error);
      alert(`Failed to save changes: ${error.message}`);
      // Always exit editing mode to avoid "stuck" cells
      setEditingCell(null);
      setEditValue('');
      fetchInspections(); // Reload to revert changes
    }
  };

  const openAdminEditModal = (row) => {
    setAdminEditModalRow(row);
  };

  // Define essential columns (always visible)
  const essentialColumns = [
    {
      key: 'thumbnail_url',
      label: 'Image',
      sortable: false,
      filterable: false,
      editable: false,
      render: (url, row) => (
        <img
          src={url}
          alt="Tag"
          className="w-16 h-16 object-cover rounded cursor-pointer hover:ring-2 hover:ring-[var(--pbfs-green)]"
          onClick={() => setSelectedImage(row.image_url)}
          loading="lazy"
        />
      )
    },
    {
      key: '_id',
      label: 'ID',
      sortable: false,
      filterable: false,
      editable: false,
      render: (val, row) => {
        const fullId = row.id || row._id || '';
        if (!fullId) return <span className="text-white/30">—</span>;
        
        // Show "tq-" prefix + first 8 chars of UUID (e.g., "tq-9a226e0d")
        const fullIdStr = fullId.toString();
        const shortId = fullIdStr.startsWith('tq-') || fullIdStr.startsWith('TQ-')
          ? fullIdStr.slice(0, 11)  // "tq-" + 8 chars
          : fullIdStr.slice(0, 8);   // Legacy IDs without prefix
        
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(fullId.toString());
              alert(`✅ Full ID copied to clipboard:\n${fullId}`);
            }}
            className="font-mono text-xs text-[var(--pbfs-blue)] hover:text-white underline cursor-pointer transition-colors"
            title={`Click to copy full ID: ${fullId}`}
          >
            {shortId}
          </button>
        );
      }
    },
    {
      key: 'all_dates',
      label: 'All Dates',
      sortable: false,
      filterable: false,
      editable: false, // Handle editing specially
      specialEdit: 'last_inspection_date', // Mark which field is editable
      render: (val, row, isEditing, setEditingCell, setEditValue, editValue, onCellSave) => {
        // Check if this specific row is being edited
        const isEditingThisDate = isEditing?.rowId === row._id && isEditing?.columnKey === 'last_inspection_date';

        if (isEditingThisDate) {
          return (
            <input
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => {
                console.log('Date picker blur - value:', editValue);
                if (editValue && editValue !== '') {
                  console.log('Saving date:', editValue);
                  onCellSave(row._id, 'last_inspection_date', editValue);
                } else {
                  console.log('No value, canceling edit');
                  setEditingCell(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  console.log('Enter pressed - saving date:', editValue);
                  onCellSave(row._id, 'last_inspection_date', editValue);
                } else if (e.key === 'Escape') {
                  console.log('Escape pressed - canceling edit');
                  setEditingCell(null);
                  setEditValue('');
                }
              }}
              autoFocus
              className="w-full px-2 py-1 bg-[var(--pbfs-navy)] border border-[var(--pbfs-green)] rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
              style={{ colorScheme: 'dark' }}
            />
          );
        }

        // Parse gemini_response to get condition and requires_attention
        let parsedData = {};
        try {
          const responseText = typeof row.gemini_response === 'string'
            ? row.gemini_response
            : JSON.stringify(row.gemini_response);
          const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
          if (jsonMatch) {
            parsedData = JSON.parse(jsonMatch[1] || jsonMatch[2]);
          }
        } catch (e) {
          // Silently fail if can't parse
        }

        const condition = parsedData.condition || 'unknown';
        const requiresAttention = parsedData.requires_attention || false;

        // Condition badge colors
        const conditionColors = {
          'Good': 'bg-green-500/20 border-green-500/40 text-green-400',
          'Fair': 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400',
          'Poor': 'bg-red-500/20 border-red-500/40 text-red-400',
          'unknown': 'bg-gray-500/20 border-gray-500/40 text-gray-400'
        };

        return (
          <div className="flex flex-col gap-1 text-xs">
            <div className="px-1 py-0.5">
              <span className="text-white/50">Captured:</span>{' '}
              <span className="text-white/80">
                {row.capture_date ? formatDate(row.capture_date, true) : '—'}
              </span>
            </div>
            <div
              data-field="last_inspection"
              className="hover:bg-[var(--pbfs-green)]/10 px-1 py-0.5 rounded cursor-pointer transition-colors z-10"
              title="Click to edit last inspection date"
              onClick={(e) => {
                e.stopPropagation();
                console.log('Last Insp clicked! Setting edit mode for row:', row._id);
                setEditingCell({ rowId: row._id, columnKey: 'last_inspection_date' });
                // Format date for input
                const dateVal = row.last_inspection_date;
                let formattedDate = '';
                if (dateVal) {
                  if (typeof dateVal === 'string') {
                    formattedDate = dateVal;
                  } else if (dateVal.year && dateVal.month && dateVal.day) {
                    formattedDate = `${dateVal.year}-${String(dateVal.month).padStart(2, '0')}-${String(dateVal.day).padStart(2, '0')}`;
                  }
                }
                console.log('Setting edit value to:', formattedDate);
                setEditValue(formattedDate);
              }}
            >
              <span className="text-white/50">Last Insp:</span>{' '}
              <span className="text-white/80">
                {row.last_inspection_date ? (typeof row.last_inspection_date === 'string' ? row.last_inspection_date : new Date(row.last_inspection_date).toLocaleDateString()) : '—'}
              </span>
            </div>
            <div className="px-1 py-0.5">
              <span className="text-white/50">Next Due:</span>{' '}
              <span className="text-white/80">
                {row.next_due_date ? (typeof row.next_due_date === 'string' ? row.next_due_date : new Date(row.next_due_date).toLocaleDateString()) : '—'}
              </span>
            </div>
            {/* 🔬 FORENSIC UNIT DATA: Condition Badge */}
            <div className="px-1 py-0.5 flex items-center gap-2">
              <span className={`px-2 py-0.5 border rounded text-xs font-medium inline-block ${conditionColors[condition] || conditionColors['unknown']}`}>
                {condition === 'Good' && '🟢 '}
                {condition === 'Fair' && '🟡 '}
                {condition === 'Poor' && '🔴 '}
                {condition}
              </span>
              {requiresAttention && (
                <span className="px-2 py-0.5 bg-orange-500/20 border border-orange-500/40 text-orange-400 rounded text-xs font-medium" title="Needs attention">
                  🚨
                </span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'site_location',
      label: 'Site Location',
      sortable: false,
      filterable: false,
      editable: false, // use popup editor (more reliable than inline)
      render: (_val, row) => {
        // Get business phone from Google Places data if available
        let businessPhone = null;
        if (row.gps_data) {
          try {
            const gpsData = typeof row.gps_data === 'string' ? JSON.parse(row.gps_data) : row.gps_data;
            businessPhone = gpsData.business_phone || null;
          } catch (e) {
            // Silently fail
          }
        }
        // Fallback: backend may store phone at top-level (manual/pin/nearby normalization)
        if (!businessPhone && row.business_phone) {
          businessPhone = row.business_phone;
        }

        return (
          <div className="flex flex-col gap-0.5 text-xs">
            <div className="font-semibold text-white/90 px-1 py-0.5">
              {row.business_name || '—'}
            </div>
            {businessPhone && (
              <div className="px-1 py-0.5">
                <a href={`tel:${businessPhone}`} className="text-[var(--pbfs-green)] hover:underline text-xs">
                  📞 {businessPhone}
                </a>
              </div>
            )}
            <div
              className="text-white/70 px-1 py-0.5 cursor-pointer hover:bg-[var(--pbfs-green)]/10 rounded transition-colors flex items-center justify-between gap-2"
              onClick={(e) => {
                e.stopPropagation();
                openAdminEditModal(row);
              }}
              title="Click to edit (popup)"
            >
              <span className="truncate">
                {row.site_street_address || row.business_address || 'Click to add address'}
              </span>
              <span className="text-white/40">✏️</span>
            </div>
            <div className="text-white/70 px-1 py-0.5">
              {[row.site_city, row.site_state, row.site_zip].filter(Boolean).join(', ') || '—'}
            </div>
          </div>
        );
      }
    },
    {
      key: 'assigned_branch',
      label: 'Nearest Branch',
      sortable: true,
      filterable: true,
      editable: true, // ✅ NOW EDITABLE with dropdown
      render: (val, row, isEditing, setEditingCell, setEditValue, editValue, onCellSave) => {
        const isShowingDropdown = branchDropdownOpen?.rowId === row._id;

        // Determine region from state
        const state = row.site_state || val?.state || '';
        let region = 'Unknown';
        if (['TX', 'OK', 'AR', 'LA'].includes(state)) region = 'Southwest';
        else if (['GA', 'FL', 'AL', 'MS', 'TN', 'SC', 'NC'].includes(state)) region = 'Southeast';
        else if (['PA', 'NY', 'NJ', 'MD', 'VA', 'WV', 'DE'].includes(state)) region = 'Mid-Atlantic';
        else if (['OH', 'IN', 'IL', 'MI', 'WI', 'MN', 'IA', 'MO'].includes(state)) region = 'Midwest';
        else if (['MA', 'CT', 'RI', 'VT', 'NH', 'ME'].includes(state)) region = 'Northeast';
        else if (['CA', 'NV', 'AZ', 'UT', 'CO', 'NM'].includes(state)) region = 'West';

        if (isShowingDropdown && branchDropdownOpen.branches) {
          return (
            <div className="relative">
              <div className="bg-[var(--pbfs-navy)] border-2 border-[var(--pbfs-green)] rounded-lg p-2 max-h-64 overflow-y-auto z-50">
                <div className="text-white/70 text-xs mb-2 font-semibold">Select Branch:</div>
                {branchDropdownOpen.branches.map((branch, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      onCellSave(row._id, 'assigned_branch', branch);
                      setBranchDropdownOpen(null);
                    }}
                    className="cursor-pointer hover:bg-[var(--pbfs-green)]/20 p-2 rounded mb-1 border border-white/10"
                  >
                    <div className="text-white font-semibold text-xs">{branch.name}</div>
                    <div className="text-white/60 text-xs">{branch.distance_km?.toFixed(1)} km away</div>
                    <div className="text-white/50 text-xs">{branch.phone}</div>
                  </div>
                ))}
                <button
                  onClick={() => setBranchDropdownOpen(null)}
                  className="w-full mt-2 px-2 py-1 bg-white/10 hover:bg-white/20 rounded text-white/70 text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        }

        if (!val || !val.name) {
          return (
            <div
              className="cursor-pointer hover:bg-[var(--pbfs-green)]/10 px-2 py-1 rounded text-white/30 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                if (row.gps_data) {
                  fetchNearbyBranches(row._id, row.gps_data);
                } else {
                  alert('⚠️ No GPS data - cannot find nearby branches');
                }
              }}
              title="Click to select branch"
            >
              Click to select branch
            </div>
          );
        }

        return (
          <div
            className="flex flex-col gap-0.5 text-xs cursor-pointer hover:bg-[var(--pbfs-green)]/10 px-1 py-0.5 rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              if (row.gps_data) {
                fetchNearbyBranches(row._id, row.gps_data);
              } else {
                alert('⚠️ No GPS data - cannot find nearby branches');
              }
            }}
            title="Click to change branch"
          >
            <div className="font-semibold text-white/90">
              {val.name}
            </div>
            {val.phone && (
              <div>
                <a href={`tel:${val.phone}`} className="text-[var(--pbfs-green)] hover:underline" onClick={(e) => e.stopPropagation()}>
                  📞 {val.phone}
                </a>
              </div>
            )}
            {val.distance_km && (
              <div className="text-white/50">
                {val.distance_km.toFixed(1)} km away
              </div>
            )}
            <div className="text-white/40 text-[10px]">
              {region} Region
            </div>
          </div>
        );
      }
    },
    // 📍 GPS Coordinates Column (for debugging location capture)
    {
      key: 'gps_coordinates',
      label: 'GPS',
      sortable: false,
      filterable: false,
      editable: false,
      render: (val, row) => {
        const gpsData = row.gps_data;
        if (!gpsData) {
          return <div className="text-white/30 text-xs italic">No GPS</div>;
        }

        const gps = typeof gpsData === 'string' ? JSON.parse(gpsData) : gpsData;
        const lat = gps.latitude;
        const lng = gps.longitude;
        const source = row.location_source || gps.source || '?';

        if (!lat || !lng) {
          return <div className="text-white/30 text-xs italic">Invalid</div>;
        }

        return (
          <div className="text-xs">
            <a
              href={`https://maps.google.com/?q=${lat},${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--pbfs-blue)] hover:underline block mb-1"
              onClick={(e) => e.stopPropagation()}
            >
              📍 {lat.toFixed(4)}, {lng.toFixed(4)}
            </a>
            <div className="text-white/40 text-[10px]">
              {source} • {gps.accuracy ? `±${Math.round(gps.accuracy)}m` : 'pin'}
            </div>
          </div>
        );
      }
    },
    // 📋 Activity Log - Enterprise tracking of ALL actions on this submission
    {
      key: 'activity_log',
      label: 'Activity',
      sortable: false,
      filterable: false,
      render: (val, inspection) => {
        const activityLog = inspection.activity_log || [];
        const latestActivity = activityLog.length > 0 ? activityLog[activityLog.length - 1] : null;

        if (!latestActivity) {
          return <span className="text-white/30 text-xs">No activity</span>;
        }

        // Activity type icons
        const activityIcons = {
          submission_created: '📝',
          email_sent: '✉️',
          email_received: '📬',
          admin_edit: '✏️',
          approval: '✅',
          auto_approved: '🤖',
          rejection: '❌',
          status_change: '🔄',
          sales_update: '💰',
          branch_assigned: '🏢',
          ai_rerun: '🔄',
          geocoded: '📍'
        };

        const icon = activityIcons[latestActivity.type] || '•';
        const action = latestActivity.action || 'Activity';
        const timestamp = latestActivity.timestamp ? new Date(latestActivity.timestamp).toLocaleDateString() : '';
        const count = activityLog.length;

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActivityLogInspection(inspection);
              setShowActivityLogModal(true);
            }}
            className="text-left hover:bg-[var(--pbfs-blue)]/10 px-2 py-1 rounded transition-colors w-full"
            title={`Click to view all ${count} activities`}
          >
            <div className="text-[var(--pbfs-blue)] text-xs font-medium flex items-center gap-1">
              <span>{icon}</span>
              <span className="truncate">{action}</span>
            </div>
            <div className="text-white/40 text-[10px] mt-0.5">
              {timestamp} • {count} {count === 1 ? 'activity' : 'activities'}
            </div>
          </button>
        );
      }
    },
    // 💰 Sales Status - Track lead progression through sales pipeline
    {
      key: 'sales_status',
      label: 'Sales',
      sortable: true,
      filterable: true,
      editable: false,
      render: (val, row) => {
        const status = (val || 'new').toLowerCase();
        const statusConfig = {
          'new': { label: 'New', color: 'bg-gray-500/20 border-gray-500/40 text-gray-400', icon: '⬜' },
          'distributed': { label: 'Sent', color: 'bg-blue-500/20 border-blue-500/40 text-blue-400', icon: '📤' },
          'contacted': { label: 'Contacted', color: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400', icon: '📞' },
          'quoting': { label: 'Quoting', color: 'bg-purple-500/20 border-purple-500/40 text-purple-400', icon: '📋' },
          'won': { label: 'Won', color: 'bg-green-500/20 border-green-500/40 text-green-400', icon: '✅' },
          'lost': { label: 'Lost', color: 'bg-red-500/20 border-red-500/40 text-red-400', icon: '❌' },
        };
        const config = statusConfig[status] || statusConfig['new'];
        
        return (
          <div 
            className="cursor-pointer hover:bg-[var(--pbfs-green)]/10 px-1 py-1 rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              openAdminEditModal(row);
            }}
            title="Click to update sales status"
          >
            <span className={`px-2 py-1 border rounded text-xs font-medium inline-flex items-center gap-1 ${config.color}`}>
              <span>{config.icon}</span>
              <span>{config.label}</span>
            </span>
            {row.sales_value && status === 'won' && (
              <div className="text-green-400 text-xs mt-1 font-medium">
                ${parseFloat(row.sales_value).toLocaleString()}
              </div>
            )}
          </div>
        );
      }
    },
    // 📝 User Notes - NOW IN ESSENTIAL COLUMNS (always visible + editable)
    {
      key: 'notes',
      label: 'Notes',
      sortable: false,
      filterable: false,
      editable: false, // use popup editor (prevents stuck inline edit + reduces RU writes)
      render: (val, inspection) => {
        // Collect all note sources
        const allNotes = [];
        const mainNotes = val || inspection.notes;
        if (mainNotes && mainNotes.trim()) {
          let cleanNotes = mainNotes;
          const submittedByMatch = mainNotes.match(/Submitted by:\s*[^\n]+\n?/i);
          if (submittedByMatch) {
            cleanNotes = mainNotes.replace(submittedByMatch[0], '').trim();
          }
          if (cleanNotes) {
            allNotes.push({ type: 'text', content: cleanNotes });
          }
        }
        if (inspection.main_page_voice_notes && inspection.main_page_voice_notes.trim()) {
          allNotes.push({ type: 'voice', content: inspection.main_page_voice_notes.trim() });
        }
        if (inspection.additional_notes && inspection.additional_notes.trim()) {
          allNotes.push({ type: 'additional', content: inspection.additional_notes.trim() });
        }

        return (
          <div
            className="max-w-xs cursor-pointer hover:bg-[var(--pbfs-green)]/10 px-2 py-1 rounded transition-colors"
            title="Click to edit (popup)"
            onClick={(e) => {
              e.stopPropagation();
              openAdminEditModal(inspection);
            }}
          >
            {allNotes.length > 0 ? (
              <div className="space-y-1">
                {allNotes.map((note, idx) => (
                  <div key={idx} className="text-white/90 text-xs whitespace-pre-wrap break-words bg-white/5 rounded px-2 py-1 border border-white/10">
                    {note.type === 'voice' ? '🎤 ' : note.type === 'additional' ? '➕ ' : '📝 '}
                    {note.content}
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-white/30 text-xs">Click to add notes</span>
            )}
          </div>
        );
      }
    }
  ];

  // Define expandable columns (shown in expanded details)
  const expandableColumns = [
    {
      key: 'current_provider',
      label: 'Current Provider',
      sortable: true,
      filterable: true,
      editable: true,
      render: (val, inspection) => {
        // Get provider address from gemini_response
        let providerAddress = null;
        try {
          const responseText = typeof inspection.gemini_response === 'string'
            ? inspection.gemini_response
            : JSON.stringify(inspection.gemini_response);
          const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[1] || jsonMatch[2]);
            providerAddress = parsed.service_company?.address;
          }
        } catch (e) {
          // Silently fail
        }

        return (
          <div className="flex flex-col gap-0.5">
            <div className="text-white/90">{val || '—'}</div>
            {providerAddress && providerAddress !== 'unknown' && (
              <div className="text-white/50 text-xs">{providerAddress}</div>
            )}
          </div>
        );
      }
    },
    // 🔬 FORENSIC UNIT: Extinguisher Type
    {
      key: 'extinguisher_type',
      label: 'Extinguisher Type',
      sortable: true,
      filterable: true,
      editable: false,
      render: (val, inspection) => {
        // Parse from gemini_response if not at top level
        let type = val || inspection.extinguisher_type;
        if (!type || type === 'unknown') {
          try {
            const responseText = typeof inspection.gemini_response === 'string'
              ? inspection.gemini_response
              : JSON.stringify(inspection.gemini_response);
            const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[1] || jsonMatch[2]);
              type = parsed.extinguisher_type || 'unknown';
            }
          } catch (e) {
            // Silently fail
          }
        }
        return <span className={type !== 'unknown' ? 'text-white/90' : 'text-white/30'}>{type || '—'}</span>;
      }
    },
    // 🔬 FORENSIC UNIT: Service Details
    {
      key: 'service_details',
      label: 'Service Details',
      sortable: false,
      filterable: false,
      editable: false,
      render: (val, inspection) => {
        try {
          const responseText = typeof inspection.gemini_response === 'string'
            ? inspection.gemini_response
            : JSON.stringify(inspection.gemini_response);
          const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[1] || jsonMatch[2]);
            const serviceDetails = parsed.service_details;
            if (serviceDetails && serviceDetails.service_type && serviceDetails.service_type !== 'unknown') {
              const additionalServices = serviceDetails.additional_services && serviceDetails.additional_services.length > 0
                ? ` (+${serviceDetails.additional_services.join(', ')})`
                : '';
              return <span className="text-white/90">{serviceDetails.service_type}{additionalServices}</span>;
            }
          }
        } catch (e) {
          // Silently fail
        }
        return <span className="text-white/30">—</span>;
      }
    },
    // 🔬 FORENSIC UNIT: Service Company Phone - NOW EDITABLE
    {
      key: 'service_company_phone',
      label: 'Provider Phone',
      sortable: false,
      filterable: false,
      editable: false, // use popup editor (more reliable)
      render: (_val, inspection) => {
        // Check all possible phone field locations
        let phone = inspection.service_company_phone || inspection.working_service_company_phone;
        if (!phone || phone === 'unknown' || phone === 'N/A') {
          try {
            const responseText = typeof inspection.gemini_response === 'string'
              ? inspection.gemini_response
              : JSON.stringify(inspection.gemini_response);
            const jsonMatch = responseText.match(/```json\\s*(\\{[\\s\\S]*?\\})\\s*```|(\\{[\\s\\S]*\\})/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[1] || jsonMatch[2]);
              phone = parsed.service_company?.phone;
            }
          } catch (_) {
            // ignore
          }
        }

        const display = phone && phone !== 'unknown' ? phone : 'Click to add';
        return (
          <div
            className={`cursor-pointer hover:bg-[var(--pbfs-green)]/10 px-2 py-1 rounded transition-colors ${phone && phone !== 'unknown' ? '' : 'text-white/30'}`}
            onClick={(e) => {
              e.stopPropagation();
              openAdminEditModal(inspection);
            }}
            title="Click to edit (popup)"
          >
            {phone && phone !== 'unknown' ? (
              <a href={`tel:${phone}`} className="text-[var(--pbfs-green)] hover:underline" onClick={(e) => e.stopPropagation()}>
                {display}
              </a>
            ) : (
              <span>{display}</span>
            )}
          </div>
        );
      }
    },
    // 🔬 FORENSIC UNIT: Equipment Numbers
    {
      key: 'equipment_numbers',
      label: 'Equipment Numbers',
      sortable: false,
      filterable: false,
      editable: false,
      render: (val, inspection) => {
        try {
          const responseText = typeof inspection.gemini_response === 'string'
            ? inspection.gemini_response
            : JSON.stringify(inspection.gemini_response);
          const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[1] || jsonMatch[2]);
            const equipment = parsed.equipment_numbers;
            if (equipment) {
              const numbers = [];
              if (equipment.ae_number && equipment.ae_number !== 'unknown') numbers.push(`AE: ${equipment.ae_number}`);
              if (equipment.he_number && equipment.he_number !== 'unknown') numbers.push(`HE: ${equipment.he_number}`);
              if (equipment.ee_number && equipment.ee_number !== 'unknown') numbers.push(`EE: ${equipment.ee_number}`);
              if (equipment.fe_number && equipment.fe_number !== 'unknown') numbers.push(`FE: ${equipment.fe_number}`);
              if (numbers.length > 0) {
                return <span className="text-white/90 text-xs font-mono">{numbers.join(' • ')}</span>;
              }
            }
          }
        } catch (e) {
          // Silently fail
        }
        return <span className="text-white/30">—</span>;
      }
    },
    // 🔬 FORENSIC UNIT: Confidence Score (QC)
    {
      key: 'confidence_score',
      label: 'AI Confidence',
      sortable: true,
      filterable: false,
      editable: false,
      render: (val, inspection) => {
        try {
          const responseText = typeof inspection.gemini_response === 'string'
            ? inspection.gemini_response
            : JSON.stringify(inspection.gemini_response);
          const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[1] || jsonMatch[2]);
            const score = parsed.confidence_score;
            if (score !== undefined && score !== null) {
              const percentage = Math.round(score * 100);
              const color = score >= 0.7 ? 'text-green-400' : score >= 0.5 ? 'text-yellow-400' : 'text-red-400';
              return (
                <span className={`${color} font-semibold`} title={`AI extraction confidence: ${percentage}%`}>
                  {percentage}%
                </span>
              );
            }
          }
        } catch (e) {
          // Silently fail
        }
        return <span className="text-white/30">—</span>;
      }
    },
    {
      key: 'submitted_by',
      label: 'Submitted By',
      sortable: true,
      filterable: true,
      editable: false, // NOT editable per user request
      render: (val, row) => {
        const name = (val || '').trim();
        const email = row.submitted_by_email || '';
        if (!name && !email) return <span className="text-white/30">—</span>;
        return (
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUserForSubmissions(name);
                setShowUserSubmissionsModal(true);
              }}
              className="text-[var(--pbfs-blue)] hover:text-white underline underline-offset-2 transition-colors text-left"
              title="Open this user's submissions"
            >
              {name}
            </button>
            {email && (
              <span className="text-white/50 text-[10px] font-mono truncate" title={email}>
                {email}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'ai_date_status',
      label: 'AI Date',
      sortable: false,
      filterable: false,
      render: (_val, row) => {
        const s = getAiDateStatus(row);
        const base = "px-2 py-1 border rounded text-xs font-semibold inline-block";
        if (s.status === 'fallback') {
          return (
            <span
              className={`${base} bg-orange-500/20 border-orange-500/40 text-orange-300`}
              title={s.reason}
            >
              Fallback
            </span>
          );
        }
        if (s.status === 'extracted') {
          return (
            <span
              className={`${base} bg-[var(--pbfs-green)]/20 border-[var(--pbfs-green)]/40 text-[var(--pbfs-green)]`}
              title={s.reason}
            >
              Extracted
            </span>
          );
        }
        return (
          <span
            className={`${base} bg-white/10 border-white/10 text-white/60`}
            title={s.reason}
          >
            Unknown
          </span>
        );
      }
    },
    // AI Analysis and Location viewing - displays data in formatted view (not raw JSON)
    {
      key: 'debug',
      label: '',
      sortable: false,
      filterable: false,
      render: (_val, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDebugModal({ type: 'ai', row });
            }}
            className="px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded text-xs text-purple-300 transition-colors"
            title="View AI analysis details"
          >
            🤖 AI
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDebugModal({ type: 'gps', row });
            }}
            className="px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded text-xs text-blue-300 transition-colors"
            title="View location details"
          >
            📍
          </button>
        </div>
      )
    }
  ];

  // Use essential columns as the main columns
  const columns = essentialColumns;

  // Handle duplicate badge click - opens DuplicateReviewModal
  const handleReviewDuplicates = async (row) => {
    try {
      console.log('🔄 Reviewing duplicate group for:', row);
      
      // If this has a duplicate_group_id, fetch all members of the group
      if (row.duplicate_group_id) {
        const dupToken = localStorage.getItem('token');
        const response = await fetch(
          `${backendUrl}/api/admin/inspections?limit=10000`,
          { headers: { 
            'X-Local-Dev': 'true',
            ...(dupToken && { 'Authorization': `Bearer ${dupToken}` })
          } }
        );
        const data = await response.json();
        
        // Find all submissions in this duplicate group
        const groupMembers = data.inspections.filter(
          insp => insp.duplicate_group_id === row.duplicate_group_id
        );
        
        console.log(`Found ${groupMembers.length} submissions in duplicate group`);
        
        // Set seed (the clicked row) and group
        setDupReviewSeed(row);
        setDupReviewGroup(groupMembers);
        setDupReviewOpen(true);
      } else {
        // No group ID - just show this one submission
        setDupReviewSeed(row);
        setDupReviewGroup([row]);
        setDupReviewOpen(true);
      }
    } catch (error) {
      console.error('Error loading duplicate group:', error);
      alert('Failed to load duplicate group. Please try again.');
    }
  };

  // Calculate navigation offset based on mode
  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4'; // Minimal offset for peek - let content flow naturally
      case 'collapsed': return 'ml-20'; // Just left margin, no width constraint
      default: return 'ml-64'; // Expanded nav - just left margin
    }
  };

  return (
    <div className={`min-h-screen overflow-hidden bg-gradient-to-br from-[#00314F] via-[#003d5c] to-[#00314F] text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />

      <UserSubmissionsModal
        isVisible={showUserSubmissionsModal}
        userName={selectedUserForSubmissions}
        onClose={() => {
          setShowUserSubmissionsModal(false);
          setSelectedUserForSubmissions(null);
        }}
      />

      <AdminEditLeadModal
        isOpen={!!adminEditModalRow}
        inspection={adminEditModalRow}
        backendUrl={backendUrl}
        onClose={() => {
          setAdminEditModalRow(null);
          if (deepLinkReturnTo) {
            navigate(deepLinkReturnTo);
          }
        }}
        onSaved={(updated) => {
          setInspections((prev) => prev.map((i) => (i._id === updated._id ? { ...i, ...updated } : i)));
        }}
      />

      {/* Duplicate Review Modal - Shows side-by-side comparison of duplicate group */}
      {dupReviewOpen && (
        <DuplicateReviewModal
          isOpen={dupReviewOpen}
          onClose={() => {
            setDupReviewOpen(false);
            setDupReviewSeed(null);
            setDupReviewGroup([]);
          }}
          seedRecord={dupReviewSeed}
          duplicateGroup={dupReviewGroup}
          reason={dupReviewSeed?.duplicate_reason || 'Duplicate group review'}
          backendUrl={backendUrl}
          onConfirmed={(result) => {
            console.log('✅ Duplicates confirmed:', result);
            // Refresh inspections to show updated duplicate status
            fetchInspections();
            setDupReviewOpen(false);
            setDupReviewSeed(null);
            setDupReviewGroup([]);
          }}
          onMarkedNotDuplicate={() => {
            console.log('✅ Marked as not duplicate');
            fetchInspections();
            setDupReviewOpen(false);
            setDupReviewSeed(null);
            setDupReviewGroup([]);
          }}
        />
      )}

      {/* Activity Log Modal - Shows full activity history */}
      {showActivityLogModal && activityLogInspection && (
        <ActivityLogModal
          isOpen={showActivityLogModal}
          inspection={activityLogInspection}
          backendUrl={backendUrl}
          onClose={() => {
            setShowActivityLogModal(false);
            setActivityLogInspection(null);
          }}
        />
      )}

      {/* AI Analysis / Location Modal - Formatted display (no raw JSON) */}
      {debugModal?.row && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
          onClick={() => setDebugModal(null)}
        >
          <div
            className="bg-[var(--pbfs-navy)] w-full max-w-4xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[var(--pbfs-green)] font-bold text-lg tracking-wide">
                  {debugModal.type === 'ai' ? '🤖 AI Analysis' : '📍 Location Details'}
                </div>
                <div className="text-white/50 text-xs mt-1">
                  {_safeText(debugModal.row?.created_at || debugModal.row?.inspection_date || '')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      const payload = debugModal.type === 'ai'
                        ? (safeParseGeminiResponse(debugModal.row) || debugModal.row?.gemini_response || '')
                        : {
                          location_source: debugModal.row?.location_source || '',
                          business_name: debugModal.row?.business_name || '',
                          business_address: debugModal.row?.business_address || '',
                          business_phone: debugModal.row?.business_phone || '',
                          gps_data: debugModal.row?.gps_data || null,
                          location: debugModal.row?.location || ''
                        };
                      const text = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
                      await navigator.clipboard.writeText(text);
                    } catch (_) {
                      // ignore
                    }
                  }}
                  className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white/80 text-xs transition-colors"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
                <button
                  onClick={() => setDebugModal(null)}
                  className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white/80 text-xs transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {debugModal.type === 'ai' ? (
                (() => {
                  const parsed = safeParseGeminiResponse(debugModal.row);
                  const text = parsed ? JSON.stringify(parsed, null, 2) : (typeof debugModal.row?.gemini_response === 'string' ? debugModal.row.gemini_response : JSON.stringify(debugModal.row?.gemini_response, null, 2));
                  const fallback = parsed?.next_due_date?.extracted_text?.includes('FALLBACK');
                  return (
                    <div>
                      {/* Rerun + Prompt controls */}
                      <div className="mb-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                          <div className="text-white/60 text-xs mb-2">AI Date Status</div>
                          {(() => {
                            const s = getAiDateStatus(debugModal.row);
                            const base = "px-2 py-1 border rounded text-xs font-semibold inline-block";
                            if (s.status === 'fallback') {
                              return <span className={`${base} bg-orange-500/20 border-orange-500/40 text-orange-300`} title={s.reason}>Fallback</span>;
                            }
                            if (s.status === 'extracted') {
                              return <span className={`${base} bg-[var(--pbfs-green)]/20 border-[var(--pbfs-green)]/40 text-[var(--pbfs-green)]`} title={s.reason}>Extracted</span>;
                            }
                            return <span className={`${base} bg-white/10 border-white/10 text-white/60`} title={s.reason}>Unknown</span>;
                          })()}
                          <div className="text-white/40 text-xs mt-2">
                            model: {debugModal.row?.model_id_used || 'unknown'} • prompt: {debugModal.row?.prompt_id || 'unknown'}
                          </div>
                        </div>

                        <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                          <div className="text-white/60 text-xs mb-2">Rerun (DEV-first)</div>
                          <input
                            value={rerunReason}
                            onChange={(e) => setRerunReason(e.target.value)}
                            placeholder="Reason (required) e.g., date failed / bad lighting"
                            className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-white/80 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/40"
                          />
                          <label className="flex items-center gap-2 mt-2 text-xs text-white/60">
                            <input
                              type="checkbox"
                              checked={rerunActivate}
                              onChange={(e) => setRerunActivate(e.target.checked)}
                            />
                            Activate rerun result (updates top-level fields)
                          </label>
                          <button
                            onClick={async () => {
                              try {
                                const reason = (rerunReason || '').trim();
                                if (!reason) {
                                  alert('Please enter a rerun reason.');
                                  return;
                                }
                                const rerunToken = localStorage.getItem('token');
                                const res = await fetch(`${backendUrl}/api/admin/inspections/${debugModal.row._id}/rerun`, {
                                  method: 'POST',
                                  headers: { 
                                    'Content-Type': 'application/json',
                                    ...(rerunToken && { 'Authorization': `Bearer ${rerunToken}` })
                                  },
                                  body: JSON.stringify({ reason, activate: rerunActivate })
                                });
                                const j = await res.json();
                                if (!res.ok || !j?.success) throw new Error(j?.detail || 'Rerun failed');
                                // Reload runs + refresh table view
                                setRerunReason('');
                                setRerunActivate(false);
                                const runsRefreshToken = localStorage.getItem('token');
                                const runsRes = await fetch(`${backendUrl}/api/admin/inspections/${debugModal.row._id}/analysis-runs`, {
                                  headers: {
                                    ...(runsRefreshToken && { 'Authorization': `Bearer ${runsRefreshToken}` })
                                  }
                                });
                                const runsJson = await runsRes.json();
                                if (runsRes.ok && runsJson?.success) {
                                  setDebugRuns({ loading: false, error: null, data: runsJson });
                                }
                                fetchInspections();
                                alert('✅ Rerun completed');
                              } catch (e) {
                                alert(`❌ Rerun failed: ${e.message}`);
                              }
                            }}
                            className="mt-3 w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white text-xs font-semibold py-2 rounded-lg transition-colors"
                          >
                            Run analysis again
                          </button>
                        </div>

                        {/* PROMPT SECTION HIDDEN - Contains JSON/code, not needed for admins */}
                        {/* TODO: Re-enable for developer debugging if needed */}
                        {false && (
                        <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                          <div className="text-white/60 text-xs mb-2">Prompt</div>
                          {debugPrompt.loading ? (
                            <div className="text-white/50 text-xs">Loading prompt…</div>
                          ) : debugPrompt.error ? (
                            <div className="text-orange-300 text-xs">{debugPrompt.error}</div>
                          ) : (
                            <>
                              <div className="text-white/80 text-xs font-semibold">
                                {debugPrompt.data?.prompt_id} • {debugPrompt.data?.prompt_version}
                              </div>
                              <button
                                onClick={() => {
                                  const sp = debugPrompt.data?.system_prompt || '';
                                  const ui = debugPrompt.data?.user_instruction || '';
                                  const combined = `SYSTEM:\\n${sp}\\n\\nUSER:\\n${ui}\\n`;
                                  // eslint-disable-next-line no-alert
                                  alert(combined.slice(0, 4000) + (combined.length > 4000 ? '\\n\\n…(truncated)\\n' : ''));
                                }}
                                className="mt-3 w-full bg-white/10 hover:bg-white/20 border border-white/10 text-white/80 text-xs font-semibold py-2 rounded-lg transition-colors"
                              >
                                View prompt (preview)
                              </button>
                            </>
                          )}
                        </div>
                        )}
                      </div>

                      {fallback && (
                        <div className="mb-4 px-4 py-3 rounded-xl border border-orange-500/40 bg-orange-500/10 text-orange-300 text-sm">
                          Date fallback detected: `next_due_date.extracted_text` indicates AI date extraction failed.
                        </div>
                      )}

                      {/* Run history */}
                      <div className="mb-4 bg-black/20 border border-white/10 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div className="text-white/80 text-sm font-semibold">Analysis runs</div>
                          <div className="text-white/40 text-xs">active: {debugRuns.data?.active_run_id || debugModal.row?.active_run_id || '—'}</div>
                        </div>
                        {debugRuns.loading ? (
                          <div className="text-white/50 text-xs mt-2">Loading runs…</div>
                        ) : debugRuns.error ? (
                          <div className="text-orange-300 text-xs mt-2">{debugRuns.error}</div>
                        ) : (
                          <div className="mt-2 space-y-2">
                            {(debugRuns.data?.analysis_runs || []).slice().reverse().slice(0, 5).map((r) => (
                              <div key={r.run_id} className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                                <div className="min-w-0">
                                  <div className="text-white/80 text-xs font-semibold truncate">
                                    {r.run_id} {r.fallback_used ? '• fallback' : '• extracted'}
                                  </div>
                                  <div className="text-white/40 text-[11px] truncate">
                                    {r.created_at} • {r.model_id} • {r.prompt_id}@{r.prompt_version} • {r.reason}
                                  </div>
                                </div>
                                <button
                                  onClick={async () => {
                                    try {
                                      const activateToken = localStorage.getItem('token');
                                      const res = await fetch(`${backendUrl}/api/admin/inspections/${debugModal.row._id}/analysis-runs/${r.run_id}/activate`, { 
                                        method: 'POST',
                                        headers: {
                                          ...(activateToken && { 'Authorization': `Bearer ${activateToken}` })
                                        }
                                      });
                                      const j = await res.json();
                                      if (!res.ok || !j?.success) throw new Error(j?.detail || 'Activate failed');
                                      fetchInspections();
                                      const runsListToken = localStorage.getItem('token');
                                      const runsRes = await fetch(`${backendUrl}/api/admin/inspections/${debugModal.row._id}/analysis-runs`, {
                                        headers: {
                                          ...(runsListToken && { 'Authorization': `Bearer ${runsListToken}` })
                                        }
                                      });
                                      const runsJson = await runsRes.json();
                                      if (runsRes.ok && runsJson?.success) setDebugRuns({ loading: false, error: null, data: runsJson });
                                      alert('✅ Activated run');
                                    } catch (e) {
                                      alert(`❌ Activate failed: ${e.message}`);
                                    }
                                  }}
                                  className="flex-shrink-0 px-2 py-1 bg-white/10 hover:bg-white/20 border border-white/10 rounded text-xs text-white/80 transition-colors"
                                  title="Activate this run"
                                >
                                  Activate
                                </button>
                              </div>
                            ))}
                            {(debugRuns.data?.analysis_runs || []).length === 0 && (
                              <div className="text-white/40 text-xs">No runs found.</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Formatted AI Analysis Display (not raw JSON) */}
                      <div className="space-y-4">
                        {parsed ? (
                          <>
                            {/* Inspection Date */}
                            <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                              <div className="text-white/60 text-xs mb-2 font-semibold">📅 Inspection Date</div>
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <div className="text-white/40 text-xs">Year</div>
                                  <div className="text-white text-lg font-bold">{parsed.last_inspection_date?.year || 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-white/40 text-xs">Month</div>
                                  <div className="text-white text-lg font-bold">{parsed.last_inspection_date?.month || 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-white/40 text-xs">Day</div>
                                  <div className="text-white text-lg font-bold">{parsed.last_inspection_date?.day || 'N/A'}</div>
                                </div>
                              </div>
                              {parsed.last_inspection_date?.extracted_text && (
                                <div className="mt-2 text-white/50 text-xs italic">{parsed.last_inspection_date.extracted_text}</div>
                              )}
                            </div>

                            {/* Next Due Date */}
                            <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                              <div className="text-white/60 text-xs mb-2 font-semibold">⏰ Next Due Date</div>
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <div className="text-white/40 text-xs">Year</div>
                                  <div className="text-white text-lg font-bold">{parsed.next_due_date?.year || 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-white/40 text-xs">Month</div>
                                  <div className="text-white text-lg font-bold">{parsed.next_due_date?.month || 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-white/40 text-xs">Day</div>
                                  <div className="text-white text-lg font-bold">{parsed.next_due_date?.day || 'N/A'}</div>
                                </div>
                              </div>
                              {fallback && (
                                <div className="mt-2 px-2 py-1 bg-orange-500/20 border border-orange-500/30 rounded text-orange-300 text-xs inline-block">⚠️ Fallback date used</div>
                              )}
                            </div>

                            {/* Equipment Details */}
                            <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                              <div className="text-white/60 text-xs mb-2 font-semibold">🧯 Equipment Details</div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <div className="text-white/40 text-xs">Extinguisher Type</div>
                                  <div className="text-white font-semibold">{parsed.extinguisher_type || 'N/A'}</div>
                                </div>
                                <div>
                                  <div className="text-white/40 text-xs">Condition</div>
                                  <div className="text-white font-semibold">{parsed.condition || 'N/A'}</div>
                                </div>
                              </div>
                            </div>

                            {/* Service Company */}
                            {parsed.service_company && (
                              <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                                <div className="text-white/60 text-xs mb-2 font-semibold">🏢 Service Company</div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="text-white/40 text-xs">Name</div>
                                    <div className="text-white font-semibold">{parsed.service_company.name || 'N/A'}</div>
                                  </div>
                                  <div>
                                    <div className="text-white/40 text-xs">Phone</div>
                                    <div className="text-white font-semibold">{parsed.service_company.phone || 'N/A'}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Tag Validation */}
                            {parsed.tag_validation && (
                              <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                                <div className="text-white/60 text-xs mb-2 font-semibold">✅ Tag Validation</div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="text-white/40 text-xs">Is Fire Extinguisher Tag?</div>
                                    <div className={`font-semibold ${parsed.tag_validation.is_fire_extinguisher_tag ? 'text-green-400' : 'text-red-400'}`}>
                                      {parsed.tag_validation.is_fire_extinguisher_tag ? 'Yes ✓' : 'No ✗'}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-white/40 text-xs">Confidence</div>
                                    <div className="text-white font-semibold">{Math.round((parsed.tag_validation.confidence || 0) * 100)}%</div>
                                  </div>
                                </div>
                                {parsed.tag_validation.reason && (
                                  <div className="mt-2 text-white/50 text-xs italic">{parsed.tag_validation.reason}</div>
                                )}
                              </div>
                            )}

                            {/* Confidence & Points */}
                            <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                              <div className="text-white/60 text-xs mb-2 font-semibold">📊 Analysis Summary</div>
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <div className="text-white/40 text-xs">Confidence Score</div>
                                  <div className="text-white text-lg font-bold">{Math.round((parsed.confidence_score || 0) * 100)}%</div>
                                </div>
                                <div>
                                  <div className="text-white/40 text-xs">Points Awarded</div>
                                  <div className="text-[var(--pbfs-green)] text-lg font-bold">{parsed.points || 0}</div>
                                </div>
                                <div>
                                  <div className="text-white/40 text-xs">Requires Attention</div>
                                  <div className={`font-semibold ${parsed.requires_attention ? 'text-orange-400' : 'text-green-400'}`}>
                                    {parsed.requires_attention ? 'Yes' : 'No'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Raw Text Analysis (if available) */}
                            {parsed.raw_text_analysis && (
                              <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                                <div className="text-white/60 text-xs mb-2 font-semibold">📝 AI Notes</div>
                                <div className="text-white/80 text-sm">{parsed.raw_text_analysis}</div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-white/50 text-sm">No AI analysis data available.</div>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                      <div className="text-white/60 text-xs mb-2">Location source</div>
                      <div className="text-white/90 text-sm font-semibold">
                        {debugModal.row?.location_source || 'unknown'}
                      </div>
                    </div>
                    <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                      <div className="text-white/60 text-xs mb-2">Business phone</div>
                      <div className="text-white/90 text-sm font-semibold">
                        {debugModal.row?.business_phone || (typeof debugModal.row?.gps_data === 'object' ? debugModal.row?.gps_data?.business_phone : '') || '—'}
                      </div>
                    </div>
                  </div>

                  {/* Formatted Location Display (not raw JSON) */}
                  <div className="space-y-4">
                    {/* Business Info */}
                    <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                      <div className="text-white/60 text-xs mb-2 font-semibold">🏢 Business Information</div>
                      <div className="space-y-3">
                        <div>
                          <div className="text-white/40 text-xs">Business Name</div>
                          <div className="text-white font-semibold text-lg">{debugModal.row?.business_name || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs">Address</div>
                          <div className="text-white">{debugModal.row?.business_address || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Site Address */}
                    <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                      <div className="text-white/60 text-xs mb-2 font-semibold">📍 Site Address</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <div className="text-white/40 text-xs">Street</div>
                          <div className="text-white font-semibold">{debugModal.row?.site_street_address || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs">City</div>
                          <div className="text-white">{debugModal.row?.site_city || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs">State</div>
                          <div className="text-white">{debugModal.row?.site_state || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs">ZIP</div>
                          <div className="text-white">{debugModal.row?.site_zip || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* GPS Coordinates */}
                    {debugModal.row?.gps_data && (
                      <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                        <div className="text-white/60 text-xs mb-2 font-semibold">🛰️ GPS Data</div>
                        <div className="grid grid-cols-2 gap-3">
                          {debugModal.row.gps_data.latitude && (
                            <div>
                              <div className="text-white/40 text-xs">Latitude</div>
                              <div className="text-white font-mono">{debugModal.row.gps_data.latitude}</div>
                            </div>
                          )}
                          {debugModal.row.gps_data.longitude && (
                            <div>
                              <div className="text-white/40 text-xs">Longitude</div>
                              <div className="text-white font-mono">{debugModal.row.gps_data.longitude}</div>
                            </div>
                          )}
                          {debugModal.row.gps_data.accuracy && (
                            <div>
                              <div className="text-white/40 text-xs">Accuracy</div>
                              <div className="text-white">{debugModal.row.gps_data.accuracy}m</div>
                            </div>
                          )}
                          {debugModal.row.gps_data.business_phone && (
                            <div>
                              <div className="text-white/40 text-xs">Business Phone</div>
                              <div className="text-white">{debugModal.row.gps_data.business_phone}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Submission Trace ID */}
                    {debugModal.row?.submission_trace_id && (
                      <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                        <div className="text-white/60 text-xs mb-2 font-semibold">🔗 Trace ID</div>
                        <div className="text-white/60 font-mono text-xs break-all">{debugModal.row.submission_trace_id}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Flex Layout for Sidebar */}
      <div className="flex pt-6 px-4 md:px-2 lg:px-1 xl:px-0.5 2xl:px-0 h-full overflow-hidden">

        {/* Collapsible Filter Sidebar - Only shows when showMobileFilters is true */}
        {showMobileFilters && (
          <div className="w-80 flex-shrink-0 border-r border-white/10 animate-slideInLeft">
            <ApprovalFilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearAllFilters}
              availableOptions={availableOptions}
              onClose={() => setShowMobileFilters(false)}
            />
          </div>
        )}

        {/* Main Content Area - Takes full width when filters hidden */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ minWidth: 0 }}>

          {/* Header with Integrated Filter Toggle */}
          <div className="flex items-center justify-between mb-3">
            {/* Left: Title + Filter Toggle */}
            <div className="flex items-center gap-3">
              {/* Filter Toggle Button - Always Visible */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className={`relative p-2 sm:p-2.5 rounded-lg transition-all duration-200 ${showMobileFilters
                  ? 'bg-[var(--pbfs-green)] text-white shadow-lg'
                  : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                  }`}
                title={showMobileFilters ? 'Hide Filters' : 'Show Filters'}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d={showMobileFilters
                      ? "M6 18L18 6M6 6l12 12"  // X icon when open
                      : "M3 4h18M3 12h18M3 20h18"  // Hamburger when closed
                    }
                  />
                </svg>
                {activeFilterCount > 0 && !showMobileFilters && (
                  <span className="absolute -top-1 -right-1 bg-[var(--pbfs-green)] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Title */}
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">Tag Leads</h1>
                <p className="text-white/60 text-xs sm:text-sm hidden sm:block">Review and approve inspection submissions</p>
              </div>
            </div>

            {/* Right: Pagination + Pye Chat + Export */}
            <div className="flex items-center gap-3">
              {/* 🪃 My Leads Toggle + Email Picker (works for both MSAL and password auth) */}
              <div className="relative flex items-center gap-1">
                <button
                  onClick={() => {
                    if (adminEmail) {
                      setMyLeadsOnly(!myLeadsOnly);
                    } else {
                      setShowEmailPicker(true);
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    myLeadsOnly 
                      ? 'bg-[var(--pbfs-green)] text-white' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                  title={adminEmail 
                    ? (myLeadsOnly ? `Showing leads assigned to ${adminEmail}` : 'Show only leads assigned to me')
                    : 'Click to set your email for filtering'
                  }
                >
                  <span>👤</span>
                  <span className="hidden sm:inline">My Leads</span>
                  {myLeadsOnly && adminEmail && (
                    <span className="bg-white/20 px-1.5 rounded text-xs">{getFilteredInspections().length}</span>
                  )}
                  {!adminEmail && <span className="text-yellow-400 text-xs">⚙️</span>}
                </button>
                
                {/* Email picker for password-auth users */}
                {adminEmail && !msalEmail && (
                  <button
                    onClick={() => setShowEmailPicker(true)}
                    className="p-1 text-white/40 hover:text-white/70"
                    title={`Filtering as: ${adminEmail}. Click to change.`}
                  >
                    ⚙️
                  </button>
                )}
                
                {/* Email picker modal */}
                {showEmailPicker && (
                  <div className="absolute top-full right-0 mt-2 bg-[#00314F] border border-white/20 rounded-lg p-3 shadow-xl z-50 min-w-[280px]">
                    <p className="text-white/70 text-xs mb-2">Enter your email to filter "My Leads":</p>
                    <input
                      type="email"
                      value={emailPickerInput}
                      onChange={(e) => setEmailPickerInput(e.target.value)}
                      placeholder="your.email@pyebarkerfs.com"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--pbfs-green)] mb-2"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const email = emailPickerInput.trim().toLowerCase();
                          if (email) {
                            localStorage.setItem('adminFilterEmail', email);
                            setStoredAdminEmail(email);
                          }
                          setShowEmailPicker(false);
                        }}
                        className="flex-1 bg-[var(--pbfs-green)] text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-[var(--pbfs-green)]/80"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          localStorage.removeItem('adminFilterEmail');
                          setStoredAdminEmail('');
                          setEmailPickerInput('');
                          setMyLeadsOnly(false);
                          setShowEmailPicker(false);
                        }}
                        className="px-3 py-1.5 bg-white/10 text-white/70 rounded text-sm hover:bg-white/20"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => setShowEmailPicker(false)}
                        className="px-3 py-1.5 text-white/50 hover:text-white text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center gap-3">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage <= 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                    currentPage <= 1
                      ? 'bg-white/5 text-white/30 cursor-not-allowed'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  <span>←</span>
                  <span className="hidden sm:inline">Prev</span>
                </button>

                {/* Page Indicator */}
                <span className="text-white/80 text-sm font-medium">
                  Page {currentPage} of {totalPages || 1}
                </span>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage >= totalPages}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                    currentPage >= totalPages
                      ? 'bg-white/5 text-white/30 cursor-not-allowed'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span>→</span>
                </button>

                {/* Divider */}
                <span className="text-white/20">|</span>

                {/* Items Per Page Dropdown */}
                <span className="text-white/60 text-sm">Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    const newLimit = parseInt(e.target.value);
                    setItemsPerPage(newLimit);
                    setCurrentPage(1); // Reset to page 1 when changing items per page
                  }}
                  className="bg-[#00314F] border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--pbfs-blue)]"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="20" className="bg-[#00314F] text-white">20</option>
                  <option value="50" className="bg-[#00314F] text-white">50</option>
                  <option value="100" className="bg-[#00314F] text-white">100</option>
                  <option value="200" className="bg-[#00314F] text-white">200</option>
                  <option value="10000" className="bg-[#00314F] text-white">All</option>
                </select>
                <span className="text-white/60 text-sm">
                  of {totalInspections}
                </span>
              </div>



              <ExportDropdown onExport={handleExport} filteredCount={totalInspections} />
              
              {/* Distribute to Regional Vice Presidents Button */}
              <button
                onClick={handleDistributeToRDs}
                disabled={loading || getFilteredInspections().filter(i => i.status === 'approved').length === 0}
                className="p-3 bg-[var(--pbfs-blue)] text-white rounded-lg hover:bg-[var(--pbfs-blue)]/80 
                           transition-all shadow-lg hover:shadow-xl transform hover:scale-105 
                           disabled:opacity-50 disabled:cursor-not-allowed"
                title="Distribute approved leads to Regional Vice Presidents via email"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send to RDs
              </button>

              {/* Email Report to Selected RVP/RD */}
              {(filters.rvpName || filters.rdName) && (
                <button
                  onClick={() => {
                    const selectedName = filters.rvpName || filters.rdName;
                    const allOfficers = [...(availableOptions.rvps || []), ...(availableOptions.rds || [])];
                    const officer = allOfficers.find(o => o.name === selectedName);
                    handleExport('csv');
                    if (officer?.email) {
                      const subject = encodeURIComponent(`TagQuest Lead Report — ${selectedName} — ${new Date().toLocaleDateString()}`);
                      const body = encodeURIComponent(
                        `Hi ${selectedName.split(' ')[0]},\n\n` +
                        `Please find attached the latest Tag Leads report filtered for your region.\n\n` +
                        `Total leads: ${totalInspections}\n` +
                        `Report generated: ${new Date().toLocaleString()}\n\n` +
                        `Best regards,\nPye-Barker Fire & Safety`
                      );
                      window.open(`mailto:${officer.email}?subject=${subject}&body=${body}`, '_blank');
                    }
                  }}
                  disabled={loading || totalInspections === 0}
                  className="p-3 bg-[var(--pbfs-green)] text-white rounded-lg hover:bg-[var(--pbfs-green)]/80
                             transition-all shadow-lg hover:shadow-xl transform hover:scale-105
                             disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  title={`Download CSV and email to ${filters.rvpName || filters.rdName}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="hidden sm:inline">Email to {filters.rvpName || filters.rdName}</span>
                  <span className="sm:hidden">Email</span>
                </button>
              )}
            </div>
          </div>

          {/* Pye AI Chat Panel */}
          {showPyeChat && (
            <div className="mb-6">
              <PyeAIChat
                backendUrl={backendUrl}
                onSearch={(query, aiFilters, results) => {
                  // Update filters based on AI interpretation
                  setFilters(prev => ({
                    ...prev,
                    query: query,
                    searchMode: 'ai'
                  }));
                  // Results will show in table automatically via fetchInspections
                }}
                currentFilters={filters}
              />
            </div>
          )}



          {/* Search Bar */}
          <div className="mb-3">
            <ApprovalSearchBar
              value={filters.query}
              onChange={(value, mode) => {
                handleFilterChange('query', value);
                if (mode) handleFilterChange('searchMode', mode);
              }}
              onSearchModeChange={(mode) => handleFilterChange('searchMode', mode)}
              onOpenChat={() => setShowPyeChat(true)} // ✅ Connected to new button
              placeholder="Search: Toggle between Fast keyword or AI semantic search..."
            />

            {/* AI Interpretation Display - LARGER, MORE VISIBLE */}
            {(aiInterpretation || aiAnswer) && (
              <div className="mt-4 p-6 bg-[var(--pbfs-green)]/10 border-2 border-[var(--pbfs-green)]/40 rounded-xl shadow-lg">
                <div className="flex items-start gap-3">
                  <span className="text-[var(--pbfs-green)] text-3xl flex-shrink-0">✨</span>
                  <div className="flex-1">
                    {/* AI Answer (for counting questions and conversations) */}
                    {aiAnswer && (
                      <div className="mb-4 p-4 bg-[var(--pbfs-green)]/20 border border-[var(--pbfs-green)]/50 rounded-lg">
                        <div className="text-white font-semibold text-lg leading-relaxed">
                          {aiAnswer}
                        </div>
                      </div>
                    )}

                    {/* AI Interpretation Breakdown - Only show if has filters */}
                    {aiInterpretation && Object.keys(aiInterpretation).length > 0 && (
                      <>
                        <div className="text-[var(--pbfs-green)] text-sm font-semibold mb-1">
                          AI Understood:
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(aiInterpretation).map(([key, value]) => (
                            <div
                              key={key}
                              className="px-3 py-1 bg-[var(--pbfs-green)]/20 border border-[var(--pbfs-green)]/30 rounded-md text-xs text-white/80"
                            >
                              <span className="font-semibold text-[var(--pbfs-green)]">
                                {key.replace(/_/g, ' ')}:
                              </span>{' '}
                              <span className="text-white">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setAiInterpretation(null);
                      setAiAnswer(null);
                    }}
                    className="text-[var(--pbfs-green)] hover:text-[var(--pbfs-green)]/70 transition-colors flex-shrink-0"
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Active Filters Indicator */}
          {activeFilterCount > 0 && (
            <div className="mb-4 flex items-center gap-3 text-sm">
              <span className="text-white/60">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
              </span>
              <span className="text-white/40">•</span>
              <span className="text-white/60">
                {totalInspections} result{totalInspections !== 1 ? 's' : ''}
              </span>
              <button
                onClick={handleClearAllFilters}
                className="ml-auto text-[var(--pbfs-green)] hover:text-[var(--pbfs-green)]/80 text-sm underline"
              >
                Clear all filters
              </button>
            </div>
          )}



          {/* Table - This should have the ONLY scrollbar */}
          <div className="flex-1 bg-[var(--pbfs-navy)]/40 backdrop-blur-sm rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
            <ExcelTable
              data={getPaginatedInspections()}
              totalCount={totalInspections} // ✅ Pass total for "X of TOTAL" display
              columns={columns}
              expandableColumns={expandableColumns}
              filters={tableFilters}
              onApprove={handleApprove}
              onReject={handleReject}
              onUndo={handleUndo}
              onDelete={handleBulkDelete}
              onArchive={handleBulkArchive}
              onExport={handleExportSelected} // ✅ NEW: Export selected inspections
              onReviewDuplicates={handleReviewDuplicates} // 🔄 Opens DuplicateReviewModal to see duplicate group
              currentStatusFilter={filters.status || 'pending'}
              loading={loading}
              editingCell={editingCell}
              setEditingCell={setEditingCell}
              editValue={editValue}
              setEditValue={setEditValue}
              onCellSave={handleCellSave}
            />
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 bg-black/80 z-[9999] flex items-end animate-fadeIn">
          <div className="w-full bg-[var(--pbfs-navy)] rounded-t-3xl max-h-[80vh] overflow-y-auto animate-slideUp">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white text-xl font-bold">Filters</h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-white/60 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
              <ApprovalFilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearAll={handleClearAllFilters}
                availableOptions={availableOptions}
                onClose={() => setShowMobileFilters(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Modal - High Resolution Viewer */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          {/* Close Button - Always Visible */}
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-[var(--pbfs-green)] text-white text-2xl w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg hover:shadow-xl z-[10000] backdrop-blur-sm border border-white/20"
            title="Close (or tap image)"
          >
            ✕
          </button>
          
          {/* Helper Text */}
          <div className="absolute top-4 left-4 bg-black/60 text-white/80 px-4 py-2 rounded-lg text-sm backdrop-blur-sm border border-white/20">
            <span className="mr-2">🔍</span>
            Pinch to zoom • Tap to close
          </div>
          
          {/* Image Container - Constrained but Zoomable */}
          <div 
            className="max-w-[95vw] max-h-[90vh] overflow-auto rounded-xl border-2 border-white/20 shadow-2xl bg-black"
            onClick={() => setSelectedImage(null)}
          >
            <img
              src={selectedImage}
              alt="High resolution tag image - Tap to close"
              className="w-auto h-auto max-w-full max-h-[88vh] object-contain rounded-lg"
              style={{ 
                minWidth: '300px',
                cursor: 'zoom-in',
                imageRendering: 'high-quality'
              }}
              onClick={() => setSelectedImage(null)}
            />
          </div>
        </div>
      )}

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

/**
 * Export Dropdown Component - Download icon with format options
 */
const ExportDropdown = ({ onExport, filteredCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportFormat = (format) => {
    onExport(format);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-3 bg-[var(--pbfs-green)] text-white rounded-lg hover:bg-[var(--pbfs-green)]/80 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
        title={`Export ${filteredCount} filtered results`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-[var(--pbfs-navy)] border border-white/20 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="p-2">
            <div className="text-white/60 text-xs px-3 py-2 uppercase tracking-wider">
              Export Format
            </div>
            <button
              onClick={() => handleExportFormat('csv')}
              className="w-full text-left px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
            >
              <span>📊</span>
              <span>CSV</span>
            </button>
            <button
              onClick={() => handleExportFormat('excel')}
              className="w-full text-left px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
            >
              <span>📗</span>
              <span>Excel (XLSX)</span>
            </button>
            <button
              onClick={() => handleExportFormat('json')}
              className="w-full text-left px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
            >
              <span>📄</span>
              <span>JSON</span>
            </button>
          </div>
          <div className="border-t border-white/10 p-2 text-white/40 text-xs text-center">
            {filteredCount} records
          </div>
        </div>
      )}
    </div>
  );
};

// Small helper to safely render non-string values in the debug modal
function _safeText(v) {
  try {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    return String(v);
  } catch (_) {
    return '';
  }
}

export default ApprovalWorkflow;
